import { resolveAuditOrg } from './lib/auditSources';
import { prepareAudit, fetchAndPrepareAudit } from './lib/auditPreparation';
import { fetchOtpYtd, auditSourceEvidence } from './lib/otpYtd';
import { matchingAck, isResolvedAck, withSavedFindingEvidence } from './lib/findingAck';
import React, { useState, useEffect, useRef, useMemo, lazy, Suspense } from 'react';
import { authFetch } from './lib/authFetch';
import { fetchRapporter } from './lib/rapporterFetch';
import { fetchPriorYearProjects } from './lib/priorYearProjects';
import { hasVatOrg, loadVatSource, vatSubmissionFor } from './lib/vatSubmission';
import { prepareApiAuditData, sourceConfig, getListEndpoint, getDataEndpoint, getTransactionsEndpoint, getClientId, normalizeClients, auditRequest, normalizeSavedSource, sourceMatchesSystem } from './lib/auditSources';
import { isAuditSaveConfirmed, auditRunId, captureAuditBaseline, auditWriteToken } from './lib/auditPersistence';
import { storedAuditInput, rerunStoredAudit, canRerun, bindToReport, restoreLastAudit, attachIssueTranslations, otherLang, reopenEngineSystem } from './lib/storedAudit';
import { pickScore, scoreBgColor, scoreTextColor, auditPresentation, reportForPresentation } from './lib/scoring';
import { t, getCurrentLang, setCurrentLang, getMonthShort, formatPeriodShort } from './lib/i18n';
import {
    getAckStore, setAcks, hasAcks,
    setAppCtx, patchAppCtx,
} from './lib/ackStore';
import { normalizeName, compactName } from './lib/constants';
import { sameBatchPeriod, selectBatchRunForPeriod, selectLatestBatchRun } from './lib/batchRunSelection';
import Icon from './components/Icon';
import FirmTimeline from './components/FirmTimeline';
import AppSidebar from './components/AppSidebar';
import { AppContext, useAppContext } from './lib/AppContext';
import ToastContainer from './components/ToastContainer';
import PdfTemplate from './report/PdfTemplate';
import { getIssueTitle } from './report/issueText';

// Engine imports
import {
    SYSTEMS, AUDIT_RULES,
    runAudit, runAuditAsync, ApiTransform, Helpers, ExcelProcessor,
    RULE_LABELS,
} from '@engine/index';

// Screen imports — code-split przez React.lazy(): kazdy ekran = osobny chunk
// ladowany dopiero przy wejsciu (mniejszy bundle startowy; raport ~216KB nie laduje sie na starcie).
// Renderowane pod <Suspense> (patrz blok widoku nizej).
const ScreenBatchDashboard = lazy(() => import('./screens/ScreenBatchDashboard'));
const ScreenHome = lazy(() => import('./screens/ScreenHome'));
const ScreenReport = lazy(() => import('./screens/ScreenReport'));
const ScreenSettings = lazy(() => import('./screens/ScreenSettings'));
const ScreenReports = lazy(() => import('./screens/ScreenReports'));
const ScreenUsers = lazy(() => import('./screens/ScreenUsers'));
const ScreenAckSummary = lazy(() => import('./screens/ScreenAckSummary'));
const ScreenAckActivity = lazy(() => import('./screens/ScreenAckActivity'));
const ScreenTracking = lazy(() => import('./screens/ScreenTracking'));
const ScreenAuditLog = lazy(() => import('./screens/ScreenAuditLog'));
const ScreenPermanentAcksLog = lazy(() => import('./screens/ScreenPermanentAcksLog'));
const ScreenMyFirms = lazy(() => import('./screens/ScreenMyFirms'));

// ---------------------------------------------------------------------------
// Inline small components not yet extracted
// ---------------------------------------------------------------------------

const ConfirmDialog = ({ open, title, message, onConfirm, onCancel, confirmLabel, danger }) => {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" role="dialog" aria-modal="true">
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-sm w-full border border-slate-200 dark:border-slate-700 animate-fade-in-up">
                <div className="p-6">
                    <h3 className="font-bold text-lg font-heading dark:text-white mb-2">{title}</h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{message}</p>
                </div>
                <div className="flex gap-3 px-6 pb-6">
                    <button onClick={onCancel}
                        className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-600 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
                        {t('confirm_cancel')}
                    </button>
                    <button onClick={onConfirm}
                        className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-bold text-white transition-colors ${danger ? 'bg-red-500 hover:bg-red-600' : 'bg-copper-500 hover:bg-copper-600'}`}>
                        {confirmLabel || t('confirm_ok')}
                    </button>
                </div>
            </div>
        </div>
    );
};

const DataPreviewModal = ({ saldo, resultat, onClose }) => {
    const [activeTab, setActiveTab] = useState('saldo');
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handleEsc);
        return () => {
            document.body.style.overflow = '';
            window.removeEventListener('keydown', handleEsc);
        };
    }, [onClose]);

    const data = activeTab === 'saldo' ? saldo : resultat;
    const filtered = searchTerm
        ? data.filter(r => r.name?.toLowerCase().includes(searchTerm.toLowerCase()) || String(r.id).includes(searchTerm))
        : data;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" role="dialog" aria-modal="true" aria-label={t('dataPreviewTitle')}>
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col border border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
                    <div className="flex items-center gap-4">
                        <h2 className="text-lg font-bold font-heading dark:text-white">{t('dataPreviewTitle')}</h2>
                        <div className="flex rounded-lg overflow-hidden border border-slate-200 dark:border-slate-600">
                            <button onClick={() => setActiveTab('saldo')} className={`px-3 py-1 text-sm font-medium transition-colors ${activeTab === 'saldo' ? 'bg-copper-500 text-white' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'}`}>
                                {t('fileSaldo')} ({saldo.length})
                            </button>
                            <button onClick={() => setActiveTab('resultat')} className={`px-3 py-1 text-sm font-medium transition-colors ${activeTab === 'resultat' ? 'bg-copper-500 text-white' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'}`}>
                                {t('fileResultat')} ({resultat.length})
                            </button>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                            placeholder={t('search_placeholder')}
                            className="px-3 py-1.5 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 dark:text-white focus:ring-2 focus:ring-copper-500 outline-none w-48" />
                        <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors">
                            <Icon name="x" className="w-5 h-5" />
                        </button>
                    </div>
                </div>
                <div className="flex-1 overflow-auto p-4">
                    <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800 z-10">
                            <tr>
                                <th className="text-left px-3 py-2 font-medium text-slate-500 dark:text-slate-400">{t('col_account')}</th>
                                <th className="text-left px-3 py-2 font-medium text-slate-500 dark:text-slate-400">{t('col_name')}</th>
                                {activeTab === 'saldo' ? (
                                    <>
                                        <th className="text-right px-3 py-2 font-medium text-slate-500 dark:text-slate-400">IB</th>
                                        <th className="text-right px-3 py-2 font-medium text-slate-500 dark:text-slate-400">UB</th>
                                    </>
                                ) : (
                                    <th className="text-right px-3 py-2 font-medium text-slate-500 dark:text-slate-400">{t('col_total')}</th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((r, i) => (
                                <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-800 border-b border-slate-100 dark:border-slate-800">
                                    <td className="px-3 py-1.5 font-mono text-slate-600 dark:text-slate-300">{r.id}</td>
                                    <td className="px-3 py-1.5 text-slate-700 dark:text-slate-200">{r.name}</td>
                                    {activeTab === 'saldo' ? (
                                        <>
                                            <td className="px-3 py-1.5 text-right font-mono text-slate-600 dark:text-slate-300">{Helpers.fmtNOK(r.ib)}</td>
                                            <td className="px-3 py-1.5 text-right font-mono text-slate-600 dark:text-slate-300">{Helpers.fmtNOK(r.ub)}</td>
                                        </>
                                    ) : (
                                        <td className="px-3 py-1.5 text-right font-mono text-slate-600 dark:text-slate-300">{Helpers.fmtNOK(r.total)}</td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {filtered.length === 0 && (
                        <div className="py-12 text-center text-slate-400">{t('no_results')}</div>
                    )}
                </div>
            </div>
        </div>
    );
};

// ManualUploadModal placeholder — imported from ScreenHome extraction or defined here
// In the monolith it is defined inside App. We'll import it if extracted, else it reads from _appCtx.
// For now, it's part of ScreenHome.jsx extraction. If not, it will be defined separately.

// ---------------------------------------------------------------------------
// ScoreGauge (inline — small, used in compare overlay)
// ---------------------------------------------------------------------------
const ScoreGauge = ({ score }) => {
    const circumference = 2 * Math.PI * 40;
    const offset = circumference - (score / 10) * circumference;
    const color = score >= 8.5 ? '#10b981' : score >= 6 ? '#f59e0b' : '#ef4444';
    return (
        <svg width="90" height="90" viewBox="0 0 100 100" className="flex-shrink-0">
            <circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" strokeWidth="8" className="text-slate-200 dark:text-slate-700" />
            <circle cx="50" cy="50" r="40" fill="none" stroke={color} strokeWidth="8" strokeLinecap="round"
                strokeDasharray={circumference} strokeDashoffset={offset}
                transform="rotate(-90 50 50)" className="gauge-ring" />
            <text x="50" y="50" textAnchor="middle" dominantBaseline="central" fill={color}
                className="font-heading" style={{ fontSize: '28px', fontWeight: 700 }}>{score}</text>
        </svg>
    );
};

// ---------------------------------------------------------------------------
// ADMIN INLINE COMPONENTS
// ---------------------------------------------------------------------------

// SNotificationStatus USUNIĘTY 2026-06-12 — zastąpiony przez ScreenTracking (widok 'admin-notifications').
// Stare endpointy /api/admin/notification-status + /notification-export zostają (przetestowane, reużywalne).

// ---------------------------------------------------------------------------
// AUDIT HISTORY COMPONENT
// ---------------------------------------------------------------------------
const SAuditHistory = () => {
  const [runs, setRuns] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const lang = getCurrentLang() === 'pl' ? 'pl' : 'no';
  const labels = lang === 'pl'
    ? { date: 'Data', period: 'Okres', firms: 'Firmy', audited: 'Zaudytowane', avg: 'Średnia', critical: 'Krytyczne', source: 'Źródło', user: 'User', notify: 'Powiadomienie' }
    : { date: 'Dato', period: 'Periode', firms: 'Firmaer', audited: 'Revidert', avg: 'Snitt', critical: 'Kritisk', source: 'Kilde', user: 'Bruker', notify: 'Varsling' };
  // P0 fix (2026-04-25): pobieram setView z context dla CTA "Run first audit"
  const ctx = useAppContext();
  const setView = ctx?.setView || (() => {});

  React.useEffect(() => {
    authFetch('/api/admin/audit-runs')
      .then(r => r.json())
      .then(d => { setRuns(d.runs || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-8 text-center text-zinc-400">{t('loading')}</div>;

  // P0 fix (2026-04-25): empty state z ikoną + CTA "Run first audit"
  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h2 className="text-xl font-bold mb-4">{t('history_title')}</h2>
      {runs.length === 0 ? (
        <div className="flex flex-col items-center py-16">
          <Icon name="history" className="w-16 h-16 text-slate-300 dark:text-slate-600 mb-4" />
          <p className="text-slate-500 dark:text-slate-400 mb-6 text-center max-w-md">{t('history_empty_desc')}</p>
          <button onClick={() => setView('batch-dashboard')}
                  className="bg-[#AA5D26] hover:bg-[#8B4A1F] text-white px-6 py-3 rounded-lg font-bold transition-colors flex items-center gap-2">
            <Icon name="play" className="w-4 h-4" />
            {t('history_run_first_audit')}
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-700 text-zinc-400 text-xs uppercase">
                <th className="py-2 px-3 text-left">{labels.date}</th>
                <th className="py-2 px-3 text-left">{labels.period}</th>
                <th className="py-2 px-3 text-center">{labels.firms}</th>
                <th className="py-2 px-3 text-center">{labels.audited}</th>
                <th className="py-2 px-3 text-center">{labels.avg}</th>
                <th className="py-2 px-3 text-center">{labels.critical}</th>
                <th className="py-2 px-3 text-left">{labels.source}</th>
                <th className="py-2 px-3 text-left">{labels.user}</th>
              </tr>
            </thead>
            <tbody>
              {runs.map(r => (
                <tr key={r.id} className="border-b border-zinc-800 hover:bg-zinc-800/50">
                  <td className="py-2 px-3 text-zinc-400">{new Date(r.created_at).toLocaleDateString('no-NO')}</td>
                  <td className="py-2 px-3">{r.period || '—'}</td>
                  <td className="py-2 px-3 text-center">{r.total_firms}</td>
                  <td className="py-2 px-3 text-center">{r.audited_firms}</td>
                  <td className="py-2 px-3 text-center">
                    <span className={r.avg_score >= 7 ? 'text-green-400' : r.avg_score >= 4 ? 'text-amber-400' : 'text-red-400'}>
                      {r.avg_score.toFixed(1)}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-center">
                    <span className={r.critical_count > 0 ? 'text-red-400 font-bold' : 'text-zinc-500'}>
                      {r.critical_count}
                    </span>
                  </td>
                  <td className="py-2 px-3">
                    <span className={`px-2 py-0.5 rounded text-xs ${r.source === 'notify' ? 'bg-blue-500/20 text-blue-400' : 'bg-zinc-700 text-zinc-400'}`}>
                      {r.source === 'notify' ? labels.notify : 'Digest'}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-zinc-400 text-xs">{r.user_email}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// MAIN APP COMPONENT
// ---------------------------------------------------------------------------

const App = () => {
    // --- ACK STORE reference ---
    const _ackStore = getAckStore();

    // --- CORE STATE ---
    const [appReady, setAppReady] = useState(false);
    const [view, setView] = useState('batch-dashboard');
    const [userRole, setUserRole] = useState(null);
    const [accountantName, setAccountantName] = useState(null);
    const [auditWindow, setAuditWindow] = useState(null);
    const [system, setSystem] = useState(null);
    const [reportSource, setReportSource] = useState(null);
    const [files, setFiles] = useState({ saldo: null, resultat: null });
    const [parsedData, setParsedData] = useState({ saldo: [], resultat: [] });
    const [report, setReport] = useState(null);
    const [globalExpand, setGlobalExpand] = useState(true);
    const [dragActive, setDragActive] = useState(false);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [toasts, setToasts] = useState([]);
    const [isAuditing, setIsAuditing] = useState(false);
    const [auditPeriod, setAuditPeriod] = useState(null);
    const [reportCompanyName, setReportCompanyName] = useState('');
    const [reportClientId, setReportClientId] = useState(null);
    // Ack state for full report view (at App level to survive re-renders)
    const [reportAcksApp, setReportAcksApp] = useState({});
    const [lang, setLangState] = useState(getCurrentLang());
    const [sidebarCollapsed, setSidebarCollapsed] = React.useState(() => {
        // P2 fix (2026-04-25): default collapsed na mobile (<768px), zachowaj user preference
        try {
            const saved = localStorage.getItem('auditor-sidebar-collapsed');
            if (saved !== null) return saved === 'true';
            return typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
        } catch(e) { return false; }
    });

    // --- 24SO API (via server proxy — API key never reaches browser) ---
    const [apiConnected, setApiConnected] = useState(false);
    const [apiClients, setApiClients] = useState([]);
    const [apiLoading, setApiLoading] = useState(false);

    // connection-config is now loaded in the appReady Promise.all above
    const [apiSelectedClient, setApiSelectedClient] = useState(null);
    const [manualUploadClient, setManualUploadClient] = useState(null);

    // Data source: '24so' | 'tripletex' | 'unimicro'
    const [dataSource, setDataSource] = useState(() => {
        try { return normalizeSavedSource(localStorage.getItem('auditor-data-source'), userRole); } catch(e) { return '24so'; }
    });
    useEffect(() => {
        const allowedSource = normalizeSavedSource(dataSource, userRole);
        if (userRole != null && dataSource !== allowedSource) setDataSource(allowedSource);
    }, [dataSource, userRole]);
    useEffect(() => {
        try { localStorage.setItem('auditor-data-source', dataSource); } catch(e) {}
    }, [dataSource]);

    // Track notification open (fire-and-forget, no auth needed)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const ntk = params.get('ntk');
        if (ntk) {
            fetch(`/api/track/${encodeURIComponent(ntk)}`).catch(() => {});
            // Clean URL without reload
            const url = new URL(window.location);
            url.searchParams.delete('ntk');
            window.history.replaceState({}, '', url.toString());
        }
    }, []);

    // Wyłączone reguły (localStorage persistence)
    const [disabledRules, setDisabledRules] = useState(() => {
        try { return JSON.parse(localStorage.getItem('audit-disabled-rules')) || []; } catch(e) { return []; }
    });
    const toggleRule = (ruleId) => {
        setDisabledRules(prev => {
            const next = prev.includes(ruleId) ? prev.filter(id => id !== ruleId) : [...prev, ruleId];
            try { localStorage.setItem('audit-disabled-rules', JSON.stringify(next)); } catch(e) {}
            return next;
        });
    };
    const setAllRules = (disabled) => {
        const next = disabled ? AUDIT_RULES.map(r => r.id) : [];
        setDisabledRules(next);
        try { localStorage.setItem('audit-disabled-rules', JSON.stringify(next)); } catch(e) {}
    };

    // P0 fix (2026-05-19): stash ostatnich inputów auditu (do re-run przy zmianie języka)
    const lastAuditInputRef = useRef(null);
    // Raport na ekranie i dane, z których wolno go przeliczyć, ustawiane zawsze razem.
    // Ekran, który podstawi raport przez samo setReport, zostawia tu dane innego raportu,
    // a canRerun odmawia wtedy przeliczenia (27.09.2026: znaleziska firmy A pod nazwą firmy B).
    const showReport = (nextReport, input = null) => {
        lastAuditInputRef.current = bindToReport(input, nextReport);
        setReportSource(input?.sourceLabel || null);
        setReport(nextReport);
    };
    const browserHistoryRef = useRef({ initialized: false, applyingPop: false, lastView: null });

    useEffect(() => {
        if (!appReady || typeof window === 'undefined' || !window.history) return;
        const nav = browserHistoryRef.current;
        const state = { ...(window.history.state || {}), audytorView: view };

        if (!nav.initialized) {
            window.history.replaceState(state, '', window.location.href);
            nav.initialized = true;
            nav.lastView = view;
            return;
        }

        if (nav.applyingPop) {
            nav.applyingPop = false;
            nav.lastView = view;
            return;
        }

        if (nav.lastView === view) return;
        window.history.pushState(state, '', window.location.href);
        nav.lastView = view;
    }, [appReady, view]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const adminViews = new Set(['admin-users', 'admin-work-activity', 'admin-notifications', 'admin-activity', 'settings', 'reports', 'home', 'batch-dashboard', 'admin-audit-history', 'permanent-acks']);
        const validViews = new Set(['my-firms', 'report', 'home', 'batch-dashboard', 'admin-work-activity', 'admin-notifications', 'reports', 'settings', 'admin-users', 'admin-audit-history', 'permanent-acks']);
        const onPopState = (event) => {
            const requested = event.state?.audytorView;
            if (!requested || !validViews.has(requested)) return;
            const nextView = userRole === 'auditor' && adminViews.has(requested) ? 'my-firms' : requested;
            browserHistoryRef.current.applyingPop = true;
            setView(nextView);
        };
        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, [userRole]);

    const setLang = (newLang) => {
        setCurrentLang(newLang);
        try { localStorage.setItem('audit-lang', newLang); } catch(e) {}
        document.documentElement.lang = newLang;
        const skip = document.getElementById('skip-link');
        if (skip) skip.textContent = newLang === 'no' ? 'Hopp til innhold' : 'Pomiń do treści';
        setLangState(newLang);
        // P0 fix (2026-05-19): regenerate frozen report text w nowym języku.
        // audit-last cache + report.issues mają title/desc/action zapieczone w starym lang
        // (engine renderuje stringi przez t() w czasie auditu). Bez re-run resolver pokazuje stary NO.
        try { localStorage.removeItem('audit-last'); } catch(e) {}
        try {
            const input = lastAuditInputRef.current;
            // Tylko gdy zapamiętane dane należą do raportu na ekranie i są z czego liczyć.
            // Raport z historii (bez sald) przeliczony tutaj dawał 10,0 i zero znalezisk.
            if (canRerun(input, report)) {
                const fresh = runAudit(
                    input.system,
                    input.saldo || [],
                    input.resultat || [],
                    input.disabledRules || [],
                    input.previousYear || null,
                    input.vatMonthly || [],
                    input.transactions || [],
                    input.txError || null,
                    input.auditMeta || null,
                );
                if (fresh && Array.isArray(fresh.issues)) {
                    showReport({ ...report, ...fresh }, input);
                }
            }
        } catch (e) { console.warn('[setLang] re-run audit failed:', e && e.message); }
    };

    // --- BATCH AUDIT STATE ---
    const [batchResults, setBatchResults] = useState([]);
    const [batchRunning, setBatchRunning] = useState(false);
    const [batchProgress, setBatchProgress] = useState({ done: 0, total: 0, current: '', rulesDone: 0, rulesTotal: 0, currentRule: '' });
    const batchAbortRef = useRef(false);
    const [batchSelectedIds, setBatchSelectedIds] = useState(new Set());
    const [batchPeriod, setBatchPeriod] = useState({ year: new Date().getFullYear(), monthFrom: 1, monthTo: new Date().getMonth() + 1 || 12 });
    const [batchHistory, setBatchHistory] = useState([]);
    const [batchExpandedId, setBatchExpandedId] = useState(null);
    const initialBatchPeriodRef = useRef(batchPeriod);
    const batchPeriodRef = useRef(batchPeriod);
    const batchResultsRef = useRef(batchResults);
    const auditStateRevisionRef = useRef(0);

    useEffect(() => { batchPeriodRef.current = batchPeriod; }, [batchPeriod.year, batchPeriod.monthFrom, batchPeriod.monthTo]);
    useEffect(() => { batchResultsRef.current = batchResults; }, [batchResults]);

    // Globalny licznik NOWYCH uwag od księgowych — alert (badge) na sidebarze, niezależnie od widoku.
    const [feedbackNewCount, setFeedbackNewCount] = useState(0);
    useEffect(() => {
        if (userRole !== 'admin') return undefined;
        let alive = true;
        const fetchCount = () => {
            authFetch('/api/admin/report-feedback?status=new')
                .then(r => (r.ok ? r.json() : null))
                .then(d => { if (alive && d) setFeedbackNewCount(d.newCount || 0); })
                .catch(() => {});
        };
        fetchCount();
        const iv = setInterval(() => { if (document.visibilityState === 'visible') fetchCount(); }, 60000);
        return () => { alive = false; clearInterval(iv); };
    }, [userRole, view]);

    // Clear ack cache when period changes — acks are per year+month range
    const prevPeriodRef = useRef(null);
    useEffect(() => {
        const key = `${batchPeriod.year}-${batchPeriod.monthFrom}-${batchPeriod.monthTo}`;
        if (prevPeriodRef.current && prevPeriodRef.current !== key) {
            const store = getAckStore();
            Object.keys(store).forEach(k => delete store[k]);
        }
        prevPeriodRef.current = key;
    }, [batchPeriod.year, batchPeriod.monthFrom, batchPeriod.monthTo]);
    const [_panelVer, _bumpPanel] = React.useReducer(x => x + 1, 0);

    // Load acks from server when panel opens — ALWAYS reload (no cache)
    useEffect(() => {
        if (!batchExpandedId) return;
        const firm = batchResults.find(r => r.identityId === batchExpandedId);
        if (!firm) return;
        const cid = firm.identityId || firm.orgNr || firm.companyName;
        if (!cid) return;
        // Force clear cache for this firm so we get fresh data for current period
        delete _ackStore[cid];
        authFetch(`/api/audit-ack?client=${encodeURIComponent(cid)}&year=${batchPeriod.year}&month=${batchPeriod.monthTo}`)
            .then(r => r.ok ? r.json() : null)
            .then(data => {
                if (data && data.acks) {
                    _ackStore[cid] = data.acks;
                    _bumpPanel();
                }
            }).catch((e) => console.error('[batchAck] LOAD error:', e));
    }, [batchExpandedId]);

    // Load acks for ALL batch firms (so table shows ack counts after refresh)
    useEffect(() => {
        if (!batchResults.length) return;
        const firmsToLoad = batchResults.filter(f => {
            const cid = f.identityId || f.orgNr || f.companyName;
            return cid && !_ackStore[cid];
        });
        if (!firmsToLoad.length) return;
        Promise.all(firmsToLoad.map(f => {
            const cid = f.identityId || f.orgNr || f.companyName;
            return authFetch(`/api/audit-ack?client=${encodeURIComponent(cid)}&year=${batchPeriod.year}&month=${batchPeriod.monthTo}`)
                .then(r => r.ok ? r.json() : null)
                .then(data => { if (data && data.acks) _ackStore[cid] = data.acks; })
                .catch(() => {});
        })).then(() => _bumpPanel());
    }, [batchResults.length, batchPeriod.year, batchPeriod.monthTo]);

    const [batchFromDashboard, setBatchFromDashboard] = useState(false);
    // [REMOVED v33] dashFilterAcct moved to activeTags in ScreenBatchDashboard
    const [confirmDialog, setConfirmDialog] = useState(null);
    const [auditLocked, setAuditLocked] = useState(false);
    // v33 — Compare Firms
    const [compareIds, setCompareIds] = useState([]);
    const [showCompare, setShowCompare] = useState(false);

    // Audit team (persisted localStorage)
    const [auditTeam, setAuditTeam] = useState(() => {
        try { return JSON.parse(localStorage.getItem('audit-team')) || []; } catch(e) { return []; }
    });
    const saveAuditTeam = (team) => {
        setAuditTeam(team);
        try { localStorage.setItem('audit-team', JSON.stringify(team)); } catch(e) {}
    };

    // Kundeliste — company → accountant mapping (auto-fetched from server + localStorage cache)
    const [kundeliste, setKundeliste] = useState(() => {
        try { return JSON.parse(localStorage.getItem('auditor-kundeliste')) || []; } catch(e) { return []; }
    });
    const saveKundeliste = (list) => {
        setKundeliste(list);
        try { localStorage.setItem('auditor-kundeliste', JSON.stringify(list)); } catch(e) {}
    };

    // Fetch user role + accountant name + audit window + connection config on mount
    useEffect(() => {
        Promise.all([
            authFetch('/api/auth/me').then(r => r.ok ? r.json() : null).catch(() => null),
            authFetch('/api/audit-window').then(r => r.ok ? r.json() : null).catch(() => null),
            authFetch('/api/connection-config').then(r => r.json()).catch(() => ({})),
        ]).then(([me, aw, cc]) => {
            if (me?.user) {
                setUserRole(me.user.role);
                setAccountantName(me.user.accountantName || null);
                // Model B (2026-04-26): auditor po loginie ląduje na "Min oversikt"
                // (admin domyślnie batch-dashboard jak dotąd).
                if (me.user.role === 'auditor' && view === 'batch-dashboard') {
                    setView('my-firms');
                }
            }
            if (aw) { setAuditWindow(aw); if (!aw.allowed) setAuditLocked(true); }
            if (cc?.configured) setApiConnected(true);
            setAppReady(true);

            // Deep link: ?report=clientId → navigate to report after data loads
            const params = new URLSearchParams(window.location.search);
            const deepClientId = params.get('report');
            if (deepClientId) {
                // Store for later — will open report once batch data is available
                window._deepLinkClientId = deepClientId;
                // Clean URL
                window.history.replaceState({}, '', window.location.pathname);
            }
        });
    }, []);

    // --- Fuzzy matching (v19.1) ---
    // Cache for findKundeMatch — cleared when kundeliste changes
    const kundeMatchCache = useRef(new Map());
    useEffect(() => { kundeMatchCache.current = new Map(); }, [kundeliste]);

    const findKundeMatch = (companyName, orgNr) => {
        if (kundeliste.length === 0) return null;
        const cacheKey = (companyName || '') + '|||' + (orgNr || '');
        if (kundeMatchCache.current.has(cacheKey)) return kundeMatchCache.current.get(cacheKey);

        let result = null;
        // Pass 0: org number match (most reliable)
        if (orgNr) {
            const cleanOrg = orgNr.replace(/\s/g, '');
            if (cleanOrg.length >= 9) {
                const orgMatch = kundeliste.find(k => k.orgNr && k.orgNr.replace(/\s/g, '') === cleanOrg);
                if (orgMatch) { kundeMatchCache.current.set(cacheKey, orgMatch); return orgMatch; }
            }
        }
        if (!companyName) { kundeMatchCache.current.set(cacheKey, null); return null; }
        const norm = companyName.trim().toLowerCase();
        // Pass 1: exact match
        const exact = kundeliste.find(k => k.name && k.name.trim().toLowerCase() === norm);
        if (exact) { kundeMatchCache.current.set(cacheKey, exact); return exact; }
        // Pass 2: normalized (strip suffixes)
        const normInput = normalizeName(companyName);
        if (!normInput) { kundeMatchCache.current.set(cacheKey, null); return null; }
        const normMatch = kundeliste.find(k => k.name && normalizeName(k.name) === normInput);
        if (normMatch) { kundeMatchCache.current.set(cacheKey, normMatch); return normMatch; }
        // Pass 3: compact (strip spaces/hyphens/&) — "Bernard Bygg" = "Bernardbygg"
        const compInput = compactName(companyName);
        if (compInput.length >= 3) {
            const compMatch = kundeliste.find(k => k.name && compactName(k.name) === compInput);
            if (compMatch) { kundeMatchCache.current.set(cacheKey, compMatch); return compMatch; }
        }
        // Pass 4: includes fallback (shorter inside longer, normalized)
        const inclMatch = kundeliste.find(k => {
            if (!k.name) return false;
            const kNorm = normalizeName(k.name);
            return kNorm.length >= 3 && normInput.length >= 3 && (kNorm.includes(normInput) || normInput.includes(kNorm));
        });
        if (inclMatch) { kundeMatchCache.current.set(cacheKey, inclMatch); return inclMatch; }
        // Pass 5: compact includes — last resort
        if (compInput.length >= 4) {
            result = kundeliste.find(k => {
                if (!k.name) return false;
                const kComp = compactName(k.name);
                return kComp.length >= 4 && (kComp.includes(compInput) || compInput.includes(kComp));
            }) || null;
        }
        kundeMatchCache.current.set(cacheKey, result);
        return result;
    };

    const getAccountant = (companyName, orgNr) => {
        const match = findKundeMatch(companyName, orgNr);
        return match ? match.accountant : '';
    };
    const getClientForm = (companyName, orgNr) => {
        const match = findKundeMatch(companyName, orgNr);
        return match ? (match.form || '') : '';
    };
    const getClientType = (companyName, orgNr) => {
        const match = findKundeMatch(companyName, orgNr);
        return match ? (match.type || '') : '';
    };
    const brregMetaCacheRef = useRef(new Map());
    const fetchBrregMeta = async (orgNr) => {
        const cleanOrg = String(orgNr || '').replace(/\s/g, '');
        if (!/^\d{9}$/.test(cleanOrg)) return null;
        const cached = brregMetaCacheRef.current.get(cleanOrg);
        if (cached && cached.expiresAt > Date.now()) return cached.value;
        try {
            const res = await authFetch(`/api/brreg/company/${encodeURIComponent(cleanOrg)}`);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            // The server bounds both company metadata and roles; a later roles read
            // must not extend the original MVA observation. Legacy responses are not cached.
            const expiresAt = Number.isFinite(data?.cacheExpiresAt) ? data.cacheExpiresAt : Date.now();
            brregMetaCacheRef.current.set(cleanOrg, { value: data, expiresAt });
            return data;
        } catch (e) {
            console.warn('[Brreg] company meta unavailable:', cleanOrg, e.message);
            brregMetaCacheRef.current.delete(cleanOrg);
            return null;
        }
    };
    const SERVICE_STOPPED_FROM = [
        { name: 'gasior malhus', from: '2026-01-01' },
    ];
    const getServiceStoppedFrom = (companyName) => {
        const nm = String(companyName || '').toLowerCase();
        return SERVICE_STOPPED_FROM.find(x => nm.includes(x.name))?.from || null;
    };
    const buildAuditMeta = (companyName, orgNr, brregMeta = null, period = null) => ({
        companyName: companyName || '',
        orgNr: orgNr || '',
        vatSubmission: vatSubmissionFor(null, orgNr, period, companyName),
        // Okres audytu dla etykiet dat na kartach („saldo na 31.08.2026"); bez niego silnik
        // bierze zakres z dat transakcji (kontrakt karty, 11.10.2026).
        period: period && Number(period.year) > 0 && Number(period.monthTo) >= 1 ? { year: Number(period.year), monthFrom: Number(period.monthFrom) || 1, monthTo: Number(period.monthTo) } : null,
        companyForm: getClientForm(companyName, orgNr),
        clientType: getClientType(companyName, orgNr),
        notServicedFrom: getServiceStoppedFrom(companyName),
        brreg: brregMeta,
        brregMvaRegistered: brregMeta && typeof brregMeta.mvaRegistered === 'boolean' ? brregMeta.mvaRegistered : null,
    });
    // Wzorzec nieoperacyjnych (holding/invest/eiendom). 2026-06-16: stemy holding*/invest*
    // łapią złożenia (Investicon, Investor, Holdingselskap) — feedback Artur: Investicon AS
    // wchodził do audytu, bo \binvest\b nie łapie "Investicon". 'eiendom' zostaje dokładne,
    // żeby nie wykluczać operacyjnych "Eiendomsmegler".
    const NON_OPERATIONAL_PATTERN = /\b(holding\w*|invest\w*|eiendom|property)\b/i;
    // Firmy jawnie oznaczone jako holding/nieoperacyjne (gdy nazwa nie łapie wzorca).
    // Dodawaj kolejne jako lowercase nazwę.
    const KNOWN_NON_OPERATIONAL = new Set([
        'investicon as',
    ]);
    const isNonOperationalClient = (companyName, orgNr) => {
        const tp = getClientType(companyName, orgNr).toLowerCase();
        const nm = (companyName || '').toLowerCase();
        return !!getServiceStoppedFrom(companyName) || KNOWN_NON_OPERATIONAL.has(nm.trim()) || tp.includes('holding') || tp.includes('eiendom') || tp.includes('investment') || NON_OPERATIONAL_PATTERN.test(nm);
    };
    const isHolding = (companyName, orgNr) => {
        return isNonOperationalClient(companyName, orgNr);
    };
    const isInactive = (companyName, orgNr) => {
        if (!companyName || kundeliste.length === 0) return false;
        const match = findKundeMatch(companyName, orgNr);
        if (match && match.active && match.active.toLowerCase() === 'inaktiv') return true;
        return (companyName || '').trim().toLowerCase().startsWith('ikke bruk');
    };
    const isAuditableClient = (companyName, orgNr) => {
        if (isNonOperationalClient(companyName, orgNr) || isInactive(companyName, orgNr)) return false;
        if (kundeliste.length === 0) return true;
        const match = findKundeMatch(companyName, orgNr);
        if (!match) return false;
        return sourceMatchesSystem(dataSource, match.system);
    };

    const fetchKundeliste = async (force = false) => {
        if (!apiConnected) return;
        try {
            const res = await authFetch(`/api/proxy/kundeliste${force ? '?force=true' : ''}`);
            if (!res.ok) { console.warn('[kundeliste] fetch failed:', res.status); return; }
            const data = await res.json();
            if (data.clients && data.clients.length > 0) {
                saveKundeliste(data.clients);
                console.log(`[kundeliste] Loaded ${data.clients.length} clients (cache: ${data.fromCache})`);
            }
        } catch (err) { console.warn('[kundeliste] fetch error:', err.message); }
    };

    // Load batch history from the online backend. The production backend is the only source of truth.
    const historyLoadedRef = useRef(false);
    useEffect(() => {
        if (!apiConnected || !userRole) return;
        let cancelled = false;
        const revision = auditStateRevisionRef.current;
        const loadHistory = async () => {
            let serverRuns = [];
            if (apiConnected) {
                try {
                    const res = await authFetch('/api/proxy/audit?limit=50');
                    if (res.ok) {
                        const data = await res.json();
                        serverRuns = (data.runs || []).map(r => ({
                            runId: r.run_id,
                            period: { year: r.period_year, monthFrom: r.period_month_from || 1, monthTo: r.period_month_to || 12 },
                            firmCount: r.total_firms,
                            avgScore: r.avg_score,
                            // created_at from API is unix seconds
                            savedAt: Number(r.created_at || 0) * 1000,
                            _server: true
                        }));
                    }
                } catch(e) { console.warn('[History] Server fetch failed:', e.message); }
            }
            if (cancelled || revision !== auditStateRevisionRef.current) return;
            serverRuns.sort((a, b) => (b.savedAt || 0) - (a.savedAt || 0));
            setBatchHistory(serverRuns);
            if (serverRuns.length > 0) historyLoadedRef.current = true;

            // Auto-restore: prefer the run matching the currently selected period. Only fall back
            // to "latest" while the period is still the initial default, so a deliberate empty
            // period is not silently overwritten by another month.
            const currentPeriod = batchPeriodRef.current || batchPeriod;
            const matchingCurrent = selectBatchRunForPeriod(serverRuns, currentPeriod, userRole);
            const mayUseLatest = sameBatchPeriod(currentPeriod, initialBatchPeriodRef.current);
            const runToRestore = matchingCurrent || (mayUseLatest ? selectLatestBatchRun(serverRuns, userRole) : null);
            if (runToRestore && batchResultsRef.current.length === 0) {
                try {
                    const data = await loadAuditRun(runToRestore.runId, runToRestore.period);
                    if (cancelled || revision !== auditStateRevisionRef.current || batchResultsRef.current.length > 0) return;
                    if (data && data.results && data.results.length > 0) {
                        markRunSynced(runToRestore.runId);
                        batchDirtyRef.current = false;
                        setBatchResults(data.results);
                        if (data.period) setBatchPeriod(data.period);
                        console.log('[History] Auto-restored', data.results.length, 'results from', runToRestore.runId);
                    }
                } catch(e) { console.warn('[History] Auto-restore failed:', e.message); }
            }
        };
        loadHistory();
        return () => { cancelled = true; };
    }, [apiConnected, userRole]);

    // Helper: load full audit run data from the online backend only.
    const loadAuditRun = async (runId, runPeriod) => {
        if (!apiConnected) return null;
        try {
            const res = await authFetch('/api/proxy/audit/' + encodeURIComponent(runId));
            if (res.ok) {
                const sdata = await res.json();
                if (sdata.results) {
                    return {
                        results: sdata.results.map(r => ({
                            runId,
                            identityId: r.identityId || r.identity_id || '',
                            companyName: r.companyName || r.company_name || '',
                            orgNr: r.orgNr || r.org_nr || r.organizationNumber || '',
                            auditedAt: r.auditedAt ?? null,
                            score: r.score || 0,
                            scoreBreakdown: r.scoreBreakdown || [],
                            totalPenalty: r.totalPenalty,
                            errCount: r.errors || 0,
                            warnCount: r.warnings || 0,
                            infoCount: r.infos || 0,
                            issues: r.issues || [],
                            dataHasActivity: (typeof r.dataHasActivity === 'number') ? r.dataHasActivity : ((typeof r.data_has_activity === 'number') ? r.data_has_activity : null),
                            dataTotals: r.dataTotals || r.data_totals || null,
                            status: 'done',
                            system: r.system || '24seven'
                        })),
                        period: runPeriod || { year: sdata.run.period_year, monthFrom: sdata.run.period_month_from || 1, monthTo: sdata.run.period_month_to || 12 }
                    };
                }
            }
        } catch(e) {
            console.warn('[History] Server load failed:', e.message);
        }
        return null;
    };

    // Period change → load matching historical run or clear results
    const periodInitRef = useRef(false);
    useEffect(() => {
        if (!apiConnected || !userRole) return;
        // Skip initial auto-restore (handled by loadHistory above)
        if (!periodInitRef.current) {
            periodInitRef.current = true;
            return;
        }
        const revision = ++auditStateRevisionRef.current;
        let cancelled = false;
        const current = () => !cancelled && revision === auditStateRevisionRef.current;
        // The previous period must disappear before an asynchronous history read.
        // A new audit may start before that read completes.
        setBatchResults([]);
        const matchingRun = selectBatchRunForPeriod(batchHistory, batchPeriod, userRole);
        if (matchingRun) {
            loadAuditRun(matchingRun.runId, matchingRun.period).then(data => {
                if (!current()) return;
                if (data && data.results && data.results.length > 0) {
                    markRunSynced(matchingRun.runId);
                    batchDirtyRef.current = false;
                    setBatchResults(data.results);
                    console.log('[Period] Loaded', data.results.length, 'results for', matchingRun.runId);
                } else {
                    setBatchResults([]);
                }
            }).catch(() => { if (current()) setBatchResults([]); });
        } else {
            setBatchResults([]);
            console.log('[Period] No historical run for selected period', batchPeriod, '— cleared results');
        }
        return () => { cancelled = true; };
    }, [batchPeriod.year, batchPeriod.monthFrom, batchPeriod.monthTo, userRole, apiConnected]);

    const clientListRevision = useRef(0);
    const sourceRef = useRef(dataSource);
    sourceRef.current = dataSource;
    // A response for the previous source must never replace the current list.
    const fetchClientList = async (source) => {
        if (!apiConnected) return null;
        const revision = ++clientListRevision.current;
        setApiLoading(true);
        try {
            const res = await authFetch(`/api/proxy${getListEndpoint(source)}`);
            if (!res.ok) throw new Error(res._hint || `HTTP ${res.status}`);
            const data = await res.json();
            if (data.error) throw new Error(data.error);
            const clients = normalizeClients(data.clients || data.companies || [], source);
            if (revision !== clientListRevision.current || sourceRef.current !== source) return null;
            setApiClients(clients);
            return clients;
        } catch(e) {
            if (revision === clientListRevision.current && sourceRef.current === source) {
                setApiClients([]);
                addToast(t('api_connection_error'), 'error');
            }
            return null;
        } finally {
            if (revision === clientListRevision.current && sourceRef.current === source) setApiLoading(false);
        }
    };
    useEffect(() => {
        setApiClients([]);
        setBatchSelectedIds(new Set());
        if (apiConnected) { fetchClientList(dataSource); fetchKundeliste(); }
        return () => { clientListRevision.current++; };
    }, [dataSource, apiConnected]);

    // FIX 6: Filter apiClients AND batchResults by kundeliste for auditors
    // Runs EVERY TIME batchResults or apiClients change — auditor always sees only their firms
    const auditorAllowedRef = useRef(null); // cache allowed names/orgs
    useEffect(() => {
        if (userRole !== 'auditor' || kundeliste.length === 0) return;

        // Rebuild allowed set every time kundeliste changes (no stale cache)
        const names = new Set(kundeliste.map(k => (k.name || '').toLowerCase().trim()).filter(Boolean));
        const orgs = new Set(kundeliste.map(k => (k.orgNr || '').replace(/\s/g, '')).filter(Boolean));
        auditorAllowedRef.current = { names, orgs };

        const match = (name, orgNr) => {
            if (orgNr && orgs.has(orgNr.replace(/\s/g, ''))) return true;
            if (name && names.has(name.toLowerCase().trim())) return true;
            return false;
        };

        // Filter apiClients
        if (apiClients.length > 0) {
            const fc = apiClients.filter(c => match(c.name || c.companyName || '', c.orgNr || c.organizationNumber || ''));
            if (fc.length < apiClients.length) {
                setApiClients(fc);
            }
        }

        // Filter batchResults — ALWAYS check, not just once
        if (batchResults.length > 0) {
            const fb = batchResults.filter(r => match(r.companyName || '', r.orgNr || ''));
            if (fb.length < batchResults.length) {
                console.log(`[Auditor] Filtered batchResults: ${batchResults.length} → ${fb.length}`);
                setBatchResults(fb);
            }
        }
    }, [kundeliste, userRole, batchResults, apiClients]);

    // Integralność danych źródłowych per firma (DODATKOWE — audit trail, nie zmienia wyniku audytu).
    // Czyste obliczenie z saldo/resultat: odcisk (fingerprint), sumy kontrolne, ruchy w okresie, reszta bilansu próbnego.
    const computeDataIntegrity = (saldo, resultat) => {
        const S = Array.isArray(saldo) ? saldo : [];
        const R = Array.isArray(resultat) ? resultat : [];
        if (S.length === 0 && R.length === 0) return null;
        const r2 = n => Math.round((Number(n) || 0) * 100) / 100;
        const saldoSum = r2(S.reduce((s, a) => s + (Number(a.ub) || 0), 0));
        const resultSum = r2(R.reduce((s, a) => s + (Number(a.sum) || 0), 0));
        const bank = r2(S.filter(a => a.accNum >= 1920 && a.accNum <= 1929).reduce((s, a) => s + (Number(a.ub) || 0), 0));
        const residual = r2(saldoSum + resultSum); // Σ ub(saldo) + Σ sum(resultat) ≈ 0 gdy bilans próbny się spina
        const hasActivity = R.some(a => Math.abs(Number(a.sum) || 0) > 0.005) || S.some(a => Math.abs(Number(a.change) || 0) > 0.005);
        const canon = JSON.stringify([
            S.map(a => [a.accNum, r2(a.ub)]).sort((x, y) => x[0] - y[0]),
            R.map(a => [a.accNum, r2(a.sum)]).sort((x, y) => x[0] - y[0]),
        ]);
        let h = 5381; for (let i = 0; i < canon.length; i++) { h = ((h << 5) + h + canon.charCodeAt(i)) >>> 0; }
        return { fingerprint: h.toString(16), totals: { accounts: S.length + R.length, saldoSum, resultSum, bank, residual }, hasActivity, balanceResidual: residual };
    };

    // Persist only audits completed in this invocation. Hydration and display changes
    // must never write cached portfolio results back to the server.
    const serverSyncedRunIds = useRef(new Set());
    const batchDirtyRef = useRef(false);
    const markRunSynced = (runId) => {
        serverSyncedRunIds.current.add(runId);
    };
    const syncBatchResultsToServer = async (resultsOverride = null, options = {}) => {
        const sourceResults = Array.isArray(resultsOverride) ? resultsOverride : batchResultsRef.current;
        const period = options.period || batchPeriodRef.current || batchPeriod;
        const runId = auditRunId(period, userRole);
        if (!options.force && !batchDirtyRef.current && serverSyncedRunIds.current.has(runId)) return true;

        const doneResults = sourceResults.filter(r => r.status === 'done');
        if (doneResults.some(r => !r.mutationId || r.saveRunId !== runId)) return false;
        if (doneResults.length === 0) return true;
        if (!apiConnected) {
            console.warn('[Audit] Server sync skipped: API disconnected');
            return false;
        }

        try {
            const resp = await authFetch('/api/proxy/audit', {
                method: 'POST',
                body: JSON.stringify({
                    runId,
                    year: Number(period.year),
                    monthFrom: Number(period.monthFrom),
                    monthTo: Number(period.monthTo),
                    language: getCurrentLang(),
                    results: doneResults.map(r => {
                        const di = computeDataIntegrity(r.saldo, r.resultat);
                        return {
                            identityId: r.identityId || '',
                            source: r.source, expectedRevision: r.expectedRevision, mutationId: r.mutationId,
                            companyName: r.companyName || '',
                            orgNr: r.orgNr || '', auditMeta: r.auditMeta || null,
                            auditedAt: r.auditedAt ?? null,
                            score: r.score || 0,
                            scoreBreakdown: r.scoreBreakdown || undefined,
                            totalPenalty: r.totalPenalty,
                            errors: r.errCount || 0,
                            warnings: r.warnCount || 0,
                            infos: r.infoCount || 0,
                            successes: r.passedRules ? r.passedRules.length : 0,
                            issues: r.issues || [],
                            system: r.system || 'Finago',
                            accountant: getAccountant(r.companyName, r.orgNr) || '',
                            dataFingerprint: di ? di.fingerprint : null,
                            dataTotals: di ? di.totals : null,
                            dataHasActivity: di ? (di.hasActivity ? 1 : 0) : null,
                            dataBalanceResidual: di ? di.balanceResidual : null,
                        };
                    })
                })
            });
            if (await isAuditSaveConfirmed(resp, runId, doneResults.length)) {
                markRunSynced(runId);
                batchDirtyRef.current = false;
                console.log('[Audit] Synced to server:', runId, doneResults.length, 'results');
                try {
                    const res = await authFetch('/api/proxy/audit?limit=50');
                    if (res.ok) {
                        const data = await res.json();
                        setBatchHistory((data.runs || []).map(r => ({
                            runId: r.run_id,
                            period: { year: r.period_year, monthFrom: r.period_month_from || 1, monthTo: r.period_month_to || 12 },
                            firmCount: r.total_firms,
                            avgScore: r.avg_score,
                            savedAt: Number(r.created_at || 0) * 1000,
                            _server: true
                        })));
                    }
                } catch(e) {}
                return true;
            }
            console.warn('[Audit] Server sync failed:', resp.status);
        } catch(e) {
            console.warn('[Audit] Server sync failed:', e.message);
        }
        return false;
    };
    // Results are written only by the explicit audit actions, per changed company.
    // Hydration, language changes and failed reruns must never resend stale firms.

    // --- SCROLL TO TOP ON VIEW CHANGE ---
    useEffect(() => {
        const main = document.getElementById('main-content');
        if (main) main.scrollTo(0, 0);
    }, [view]);

    // --- KEYBOARD SHORTCUTS ---
    useEffect(() => {
        const handler = (e) => {
            // Don't trigger when typing in inputs
            const tag = e.target.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
            if (e.key === '/') {
                e.preventDefault();
                // Focus the search input on the active view
                const searchInput = document.querySelector('main input[type="text"]');
                if (searchInput) searchInput.focus();
            }
            if (e.key === 'Escape') {
                if (showCompare) { setShowCompare(false); return; }
                if (confirmDialog) { setConfirmDialog(null); return; }
                if (showPreviewModal) { setShowPreviewModal(false); return; }
                if (batchExpandedId) { setBatchExpandedId(null); return; }
            }
            // Ctrl+1..5 — quick navigation
            if (e.ctrlKey && !e.shiftKey && !e.altKey && !e.metaKey) {
                const viewMap = { '1': 'batch-dashboard', '2': 'admin-work-activity', '3': 'reports', '4': 'home', '5': 'settings' };
                const target = viewMap[e.key];
                if (target) {
                    e.preventDefault();
                    // Guard: auditors can only access my-firms (admin-only views blocked)
                    const adminViews = ['admin-users', 'admin-work-activity', 'admin-activity', 'settings', 'reports', 'home', 'batch-dashboard'];
                    if (userRole !== 'admin' && adminViews.includes(target)) return;
                    setView(target);
                }
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [confirmDialog, showCompare, showPreviewModal, batchExpandedId, userRole]);

    // --- BATCH EXECUTION ENGINE ---
    // DATA INTEGRITY: Sequential processing only (concurrency=1).
    // 24SO SOAP API shares a single session — concurrent switchClient() corrupts data.
    // NEVER increase concurrency above 1 without solving the session isolation problem.
    const batchStartLock = useRef(false);
    const runBatchWithPool = async (clients) => {
        if (batchStartLock.current) return;
        const auditableClients = (clients || []).filter(client => {
            const name = client.name || client.clientName || client.companyName || '';
            const orgNr = resolveAuditOrg(client, kundeliste, apiClients);
            return isAuditableClient(name, orgNr);
        });
        const skipped = (clients || []).length - auditableClients.length;
        if (skipped > 0) {
            addToast(`${skipped} firm holding/invest/eiendom/inaktywnych pominięto w audycie`, 'warning');
        }
        if (auditableClients.length === 0) {
            addToast('Brak firm operacyjnych do audytu po filtrze kundeliste.', 'warning');
            return;
        }
        batchStartLock.current = true;
        setBatchRunning(true);
        let saveBaseline;
        try { saveBaseline = await captureAuditBaseline(authFetch, batchPeriod, userRole); }
        catch (e) { batchStartLock.current = false; setBatchRunning(false); addToast(e.message, 'error'); return; }
        batchDirtyRef.current = true;
        setBatchRunning(true);
        auditStateRevisionRef.current++;
        // Keep the last valid result until its replacement is calculated successfully.
        let accumulatedBatchResults = [...batchResultsRef.current];
        const completedThisRun = [];
        const pendingWrites = [];
        let failedAttempts = 0;
        let hadProgressSyncError = false;
        setBatchResults(accumulatedBatchResults);
        batchAbortRef.current = false;
        const total = auditableClients.length;
        setBatchProgress({ done: 0, total, current: '', rulesDone: 0, rulesTotal: 0, currentRule: '' });
        const startTime = Date.now();
        window._batchStartTime = startTime;

        const vatSource = auditableClients.some(client => hasVatOrg(resolveAuditOrg(client, kundeliste, apiClients)))
            ? await loadVatSource(authFetch, userRole, batchPeriod) : null;

        // SEQUENTIAL — one company at a time, no parallel workers
        for (let i = 0; i < auditableClients.length; i++) {
            if (batchAbortRef.current) break;
            const client = auditableClients[i];
            const expectedName = client.name || client.clientName || client.companyName || '';
            setBatchProgress(prev => ({ ...prev, done: i + 1, total, current: expectedName, rulesDone: 0, rulesTotal: 0, currentRule: '' }));
            try {
                const reqBody = JSON.stringify(auditRequest(client, dataSource, batchPeriod));

                // STEP 1: Fetch aggregated data
                const stopOnAbort = { isAborted: () => batchAbortRef.current };
                let strictData, strictLines;
                const strictExpected = {identityId:client.identityId,clientName:expectedName,year:batchPeriod.year,monthFrom:batchPeriod.monthFrom,monthTo:batchPeriod.monthTo};
                const strictPrepared = dataSource === '24so' ? await fetchAndPrepareAudit({
                    source:dataSource,request:JSON.parse(reqBody),expected:strictExpected,
                    fetchFn:async (p,b) => {const value=await fetchRapporter(p,b,stopOnAbort); if(p==='/audit-data')strictData=value;else strictLines=value;return value;}
                }) : null;
                if (strictPrepared && !strictPrepared.input) throw new Error(strictPrepared.status === 'no_data' ? 'Brak danych księgi; poprzedni raport pozostaje bez zmian.' : 'Niepotwierdzone dane audytu: '+strictPrepared.blockers.map(b=>b.code).join(', '));
                const data = strictData || await fetchRapporter(getDataEndpoint(dataSource), reqBody, stopOnAbort);

                // DATA INTEGRITY BARRIER: Verify API returned data for the correct company
                const apiClientName = (data.client || '').trim().toLowerCase();
                const expClientName = expectedName.trim().toLowerCase();
                if (apiClientName && expClientName && apiClientName !== expClientName) {
                    const msg = `DATA MISMATCH! Expected "${expectedName}" but API returned "${data.client}". Skipping to prevent data contamination.`;
                    console.error(`[Audit] ${msg}`);
                    throw new Error(msg);
                }

                let saldo = ApiTransform.transformSaldo(data.saldo);
                let resultat = ApiTransform.transformResultat(data.resultat, data.months);
                let previousYear = ApiTransform.transformPreviousYear(data.previousYear, data.months);
                let vatMonthly = data.vatMonthly || [];

                // STEP 2: Fetch transactions (sequential, NOT parallel with step 1)
                let transactions = [];
                let txError = null;
                let transactionResponse = strictLines || null;
                try {
                    const txData = strictLines || await fetchRapporter(getTransactionsEndpoint(dataSource), reqBody, stopOnAbort);
                    transactionResponse = txData;

                    // DATA INTEGRITY BARRIER: Verify transactions are for the correct company
                    const txClientName = (txData.client || '').trim().toLowerCase();
                    if (txClientName && expClientName && txClientName !== expClientName) {
                        const msg = `TRANSACTION DATA MISMATCH! Expected "${expectedName}" but got "${txData.client}". Discarding transactions.`;
                        console.error(`[Audit] ${msg}`);
                        txError = msg;
                    } else {
                        transactions = txData.transactions || [];
                        console.log(`[Audit] ${expectedName}: ${transactions.length} transactions fetched`);
                    }
                } catch(e) {
                    txError = e.message;
                    console.warn(`[Audit] ${expectedName}: get-transactions failed:`, e.message);
                }
                // Bieg przerwany, a linii księgi nie ma: nie zapisujemy audytu z połowy danych jako gotowego.
                if (txError && batchAbortRef.current) break;

                ({ saldo, resultat, previousYear, vatMonthly, transactions } = strictPrepared ? strictPrepared.input : prepareApiAuditData(data, transactionResponse, {
                    clientName: expectedName, companyId: client.companyId, companyKey: client.companyKey, year: batchPeriod.year,
                    monthFrom: batchPeriod.monthFrom, monthTo: batchPeriod.monthTo,
                }, dataSource));

                const orgNr = resolveAuditOrg(client, kundeliste, apiClients);
                const brregMeta = await fetchBrregMeta(orgNr);
                // Projekty z poprzedniego roku: reguła project_pnl_summary odróżnia nimi projekt
                // rozpoczęty w tym roku od ciągnącego się z poprzedniego (11.10.2026).
                const priorYearProjects = await fetchPriorYearProjects({ fetchFn: (p, b, o) => fetchRapporter(p, b, { ...stopOnAbort, ...o }),
                    source: dataSource, request: JSON.parse(reqBody), transactions, isAborted: stopOnAbort.isAborted });
                // Przerwanie w trakcie tego odczytu: nie zapisujemy audytu z „niedostępne" jako gotowego.
                if (batchAbortRef.current) break;
                const otpYtd = await fetchOtpYtd({fetchFn:fetchRapporter, source:dataSource, request:{...JSON.parse(reqBody),...(dataSource==='24so'?{auditContract:'audit-data/v1'}:{})}, isAborted:stopOnAbort.isAborted});
                if (batchAbortRef.current) break;
                let auditMeta = { ...buildAuditMeta(expectedName, orgNr, brregMeta, batchPeriod), priorYearProjects, otpYtd, sourceEvidence:auditSourceEvidence(dataSource,JSON.parse(reqBody),data,transactionResponse), source:dataSource, sourceIdentity:String(JSON.parse(reqBody)[sourceConfig(dataSource).idField] || ''),
                    vatSubmission: vatSubmissionFor(vatSource, orgNr, batchPeriod, expectedName) };
                if (strictPrepared) auditMeta = prepareAudit({source:dataSource,data,lines:transactionResponse,expected:strictExpected,meta:auditMeta}).auditMeta;
                const auditReport = await runAuditAsync('24seven', saldo, resultat, disabledRules, previousYear, vatMonthly, transactions, txError, (ruleIdx, rulesTotal, ruleLabel) => {
                    setBatchProgress(prev => ({ ...prev, rulesDone: ruleIdx, rulesTotal, currentRule: ruleLabel }));
                }, auditMeta);
                auditReport.issues = attachIssueTranslations(auditReport.issues, otherLang(), () => runAudit('24seven', saldo, resultat, disabledRules, previousYear, vatMonthly, transactions, txError, auditMeta));
                const auditedResult = {
                    ...auditWriteToken(saveBaseline, getClientId(client, dataSource)), source: dataSource,
                    identityId: getClientId(client, dataSource),
                    companyName: expectedName,
                    orgNr,
                    auditMeta,
                    companyForm: auditMeta.companyForm,
                    apiClientName: data.client || '',
                    auditedAt: Date.now(),
                    score: auditReport.score,
                    scoreBreakdown: auditReport.scoreBreakdown,
                    totalPenalty: auditReport.totalPenalty,
                    errCount: auditReport.errCount,
                    warnCount: auditReport.warnCount,
                    infoCount: auditReport.infoCount,
                    issues: auditReport.issues,
                    categorySummary: auditReport.categorySummary,
                    passedRules: auditReport.passedRules,
                    saldo, resultat, vatMonthly, transactions,
                    // Tylko w pamięci, dla ponownego liczenia (lib/storedAudit.js). Na serwer nie idą.
                    previousYear, txError,
                    status: 'done',
                    reviewed: false,
                    assignee: '',
                    notes: '',
                    system: sourceConfig(dataSource).label
                };
                accumulatedBatchResults = accumulatedBatchResults
                    .filter(r => r.identityId !== auditedResult.identityId)
                    .concat(auditedResult);
                completedThisRun.push(auditedResult);
                if (sameBatchPeriod(batchPeriodRef.current, batchPeriod)) setBatchResults(accumulatedBatchResults);
                const savedProgress = await syncBatchResultsToServer([auditedResult], { force: true, period: batchPeriod });
                if (!savedProgress) { hadProgressSyncError = true; pendingWrites.push(auditedResult); }
            } catch(err) {
                failedAttempts++;
                const priorResult = accumulatedBatchResults.find(r => r.identityId === getClientId(client, dataSource) && r.status === 'done');
                const errorResult = {
                    identityId: getClientId(client, dataSource),
                    companyName: expectedName,
                    orgNr: client.organizationNumber || client.orgNumber || '',
                    score: 0, errCount: 0, warnCount: 0, infoCount: 0,
                    issues: [], categorySummary: {}, passedRules: [],
                    saldo: [], resultat: [],
                    status: 'error',
                    reviewed: false,
                    assignee: '',
                    notes: '',
                    system: sourceConfig(dataSource).label,
                    errorMsg: err.message
                };
                if (priorResult) {
                    addToast(`${expectedName}: ${err.message}`, 'error');
                } else {
                    accumulatedBatchResults = accumulatedBatchResults
                        .filter(r => r.identityId !== errorResult.identityId)
                        .concat(errorResult);
                }
                if (sameBatchPeriod(batchPeriodRef.current, batchPeriod)) setBatchResults(accumulatedBatchResults);
            }
            // Small delay between companies to let 24SO session settle
            await new Promise(r => setTimeout(r, 300));
        }
        await new Promise(r => setTimeout(r, 250));
        let batchSaved = true;
        for (const pending of pendingWrites) {
            if (!await syncBatchResultsToServer([pending], {force:true,period:batchPeriod})) batchSaved=false;
        }
        batchStartLock.current=false;
        setBatchRunning(false);
        if (!batchSaved) {
            addToast('Zapis historii audytu nie powiódł się - nie wysyłaj powiadomień przed ponowną próbą.', 'error');
        }
        if (!batchAbortRef.current) {
            addToast(t('batch_done', completedThisRun.length), failedAttempts > 0 ? 'warning' : 'success');
            // Browser notification
            if (document.hidden && 'Notification' in window && Notification.permission === 'granted') {
                new Notification(t('batch_done', completedThisRun.length));
            }
            // Results are saved. Email requires a separate preview and explicit user action.
        } else {
            addToast(t('batch_aborted'), 'warning');
        }
    };

    // Re-run audytu przy zmianie języka lub reguł — regeneruje opisy w nowym języku
    useEffect(() => {
        // Re-run currently viewed report, tylko z danych przypiętych do tego raportu.
        // Bez nich raport zostaje taki, jaki jest: uproszczone liczenie z parsedData gubiło
        // rok poprzedni, MVA i transakcje, a po otwarciu innej firmy liczyło cudze salda.
        const input = lastAuditInputRef.current;
        if (canRerun(input, report)) {
            const result = runAudit(input.system, input.saldo, input.resultat, disabledRules, input.previousYear || null, input.vatMonthly || [], input.transactions || [], input.txError || null, input.auditMeta || null);
            showReport({ ...result, auditedAt: report.auditedAt ?? null }, { ...input, disabledRules });
        }
        // Re-run batch results to update translated strings (admin AND auditor — bug fix 2026-05-19)
        if (batchResults.length > 0) {
            const updated = batchResults.map(r => {
                if (r.status !== 'done' || !r.saldo || r.saldo.length === 0) return r;
                try {
                    const auditMeta = r.auditMeta || buildAuditMeta(r.companyName, r.orgNr);
                    const result = rerunStoredAudit(r, disabledRules, auditMeta);
                    return { ...r, issues: result.issues, score: result.score, scoreBreakdown: result.scoreBreakdown, totalPenalty: result.totalPenalty, errCount: result.errCount, warnCount: result.warnCount, infoCount: result.infoCount, passedRules: result.passedRules, categorySummary: result.categorySummary };
                } catch { return r; }
            });
            setBatchResults(updated);
        }
    }, [lang, disabledRules]);

    // P0 fix (2026-04-25): public funkcja do otwierania raportu firmy z dashboard.
    // Bezpośrednio wywoływalna przez context.openFirmReport(firm) — bez window._deepLinkClientId tricku.
    const openFirmReport = (firm) => {
        if (!firm || firm.status !== 'done') return;
        setReportCompanyName(firm.companyName || '');
        setReportClientId(firm.identityId || firm.orgNr || null);
        setAuditPeriod(batchPeriod);
        setBatchFromDashboard(true);
        setSystem(reopenEngineSystem(firm));
        setFiles({ saldo: `Batch: ${firm.companyName}`, resultat: `Batch: ${firm.companyName}` });
        showStoredAudit(firm, { issues: firm.issues || [], score: firm.score, errCount: firm.errCount, warnCount: firm.warnCount });
        setView('report');
        setGlobalExpand(true);
    };

    // Raport otwierany z wyniku w pamięci: pokazuje go razem z danymi tej samej firmy.
    const showStoredAudit = (firm, nextReport) => {
        const input = storedAuditInput(firm, disabledRules, firm.auditMeta || buildAuditMeta(firm.companyName, firm.orgNr));
        setParsedData({ saldo: input.saldo, resultat: input.resultat });
        showReport({ scoreBreakdown: firm.scoreBreakdown, totalPenalty: firm.totalPenalty, presentationRetiredRules:firm.presentationRetiredRules, notCheckedRules:firm.notCheckedRules, coverage_complete:firm.coverage_complete, unchecked_count:firm.unchecked_count, ...nextReport, auditedAt: firm.auditedAt ?? null }, input);
    };

    // Deep link: open firm report when batch data loads
    const deepLinkTimerRef = useRef(null);
    useEffect(() => {
        const cid = window._deepLinkClientId;
        if (!cid) return;

        // If batchResults available, try to find the firm
        if (batchResults.length > 0) {
            const firm = batchResults.find(r =>
                r.identityId === cid || r.orgNr === cid || (r.companyName || '').toLowerCase() === cid.toLowerCase()
            );
            if (firm && firm.status === 'done') {
                window._deepLinkClientId = null;
                if (deepLinkTimerRef.current) clearTimeout(deepLinkTimerRef.current);
                setReportCompanyName(firm.companyName || '');
                setReportClientId(firm.identityId || firm.orgNr || null);
                setAuditPeriod(batchPeriod);
                setBatchFromDashboard(true);
                setSystem('24seven');
                setFiles({ saldo: `Batch: ${firm.companyName}`, resultat: `Batch: ${firm.companyName}` });
                showStoredAudit(firm, { issues: firm.issues || [], score: firm.score, errCount: firm.errCount, warnCount: firm.warnCount });
                setView('report');
                setGlobalExpand(true);
                return;
            }
        }

        // Fallback: if batchResults still empty after 3s, try fetching from server
        if (batchResults.length === 0 && apiConnected && !deepLinkTimerRef.current) {
            deepLinkTimerRef.current = setTimeout(async () => {
                deepLinkTimerRef.current = null;
                const currentCid = window._deepLinkClientId;
                if (!currentCid) return;
                try {
                    const res = await authFetch('/api/proxy/audit?limit=10');
                    if (!res.ok) return;
                    const data = await res.json();
                    const runs = data.runs || [];
                    for (const run of runs) {
                        const runData = await loadAuditRun(run.run_id || run.runId, {
                            year: run.period_year, monthFrom: run.period_month_from || 1, monthTo: run.period_month_to || 12
                        });
                        if (!runData || !runData.results) continue;
                        const firm = runData.results.find(r =>
                            r.identityId === currentCid || r.orgNr === currentCid ||
                            (r.companyName || '').toLowerCase() === currentCid.toLowerCase()
                        );
                        if (firm && firm.status === 'done') {
                            window._deepLinkClientId = null;
                            setBatchResults(runData.results);
                            if (runData.period) setBatchPeriod(runData.period);
                            setReportCompanyName(firm.companyName || '');
                            setReportClientId(firm.identityId || firm.orgNr || null);
                            setAuditPeriod(runData.period || batchPeriod);
                            setBatchFromDashboard(true);
                            setSystem('24seven');
                            setFiles({ saldo: `Batch: ${firm.companyName}`, resultat: `Batch: ${firm.companyName}` });
                            showStoredAudit(firm, { issues: firm.issues || [], score: firm.score, errCount: firm.errCount, warnCount: firm.warnCount });
                            setView('report');
                            setGlobalExpand(true);
                            return;
                        }
                    }
                    console.warn('[DeepLink] Firm not found in server history:', currentCid);
                    window._deepLinkClientId = null;
                } catch (e) {
                    console.warn('[DeepLink] Server fetch failed:', e.message);
                    window._deepLinkClientId = null;
                }
            }, 3000);
        }
    }, [batchResults, apiConnected]);

    // Czyść dane finansowe przy zamknięciu karty (bezpieczeństwo na shared PC)
    useEffect(() => {
        const cleanup = () => {
            try {
                localStorage.removeItem('audit-last');
            } catch(e) {}
        };
        window.addEventListener('beforeunload', cleanup);
        return () => window.removeEventListener('beforeunload', cleanup);
    }, []);

    const addToast = (message, type = 'error') => {
        const id = Date.now() + Math.random();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 6000);
    };
    const removeToast = (id) => setToasts(prev => prev.filter(t => t.id !== id));

    const handleSystemSelect = (sysKey) => {
        setSystem(sysKey);
        setFiles({ saldo: null, resultat: null });
        setParsedData({ saldo: [], resultat: [] });
        showReport(null);
        setAuditPeriod(null);
    };

    // --- API: Connect via server proxy (fetch client list) ---
    const handleApiConnect = async () => {
        const clients = await fetchClientList(dataSource);
        if (clients) {
            setApiSelectedClient(null);
            addToast(t('settings_api_status_connected', clients.length), 'success');
        }
    };

    // --- API: Fetch data & run audit ---
    const handleApiFetchAndAudit = async (client, year, monthFrom, monthTo) => {
        auditStateRevisionRef.current++;
        setApiLoading(true);
        try {
            const saveBaseline = await captureAuditBaseline(authFetch, {year:Number(year),monthFrom:Number(monthFrom),monthTo:Number(monthTo)}, userRole);
            batchDirtyRef.current = true;
            const reqBody = JSON.stringify(auditRequest(client, dataSource, {year,monthFrom,monthTo}));
            const expectedName = client.name || client.clientName || client.companyName || '';
            const expectedOrgNr = client.organizationNumber || client.orgNumber || '';
            if (!isAuditableClient(expectedName, expectedOrgNr)) {
                addToast('Firma spoza aktywnej kundeliste/Finago albo holding/inaktywna została pominięta w audycie.', 'warning');
                return;
            }

            // SEQUENTIAL fetch — aggregated first, then transactions (prevent session contamination)
            let strictData, strictLines;
            const strictExpected = {identityId:client.identityId,clientName:expectedName,year,monthFrom,monthTo};
            const strictPrepared = dataSource === '24so' ? await fetchAndPrepareAudit({
                source:dataSource,request:JSON.parse(reqBody),expected:strictExpected,
                fetchFn:async (p,b) => {const value=await fetchRapporter(p,b);if(p==='/audit-data')strictData=value;else strictLines=value;return value;}
            }) : null;
            if (strictPrepared && !strictPrepared.input) throw new Error(strictPrepared.status === 'no_data' ? 'Brak danych księgi; poprzedni raport pozostaje bez zmian.' : 'Niepotwierdzone dane audytu: '+strictPrepared.blockers.map(b=>b.code).join(', '));
            const data = strictData || await fetchRapporter(getDataEndpoint(dataSource), reqBody);

            // DATA INTEGRITY BARRIER: Verify company name matches
            const apiName = (data.client || '').trim().toLowerCase();
            const expName = expectedName.trim().toLowerCase();
            if (apiName && expName && apiName !== expName) {
                throw new Error(`DATA MISMATCH! Expected "${expectedName}" but API returned "${data.client}". Aborting.`);
            }

            // Fetch transactions (sequential, after aggregated)
            let transactions = [];
            let txError = null;
            let transactionResponse = strictLines || null;
            try {
                const txData = strictLines || await fetchRapporter(getTransactionsEndpoint(dataSource), reqBody);
                transactionResponse = txData;
                // DATA INTEGRITY BARRIER for transactions
                const txName = (txData.client || '').trim().toLowerCase();
                if (txName && expName && txName !== expName) {
                    txError = `TX DATA MISMATCH: expected "${expectedName}", got "${txData.client}"`;
                    console.error(`[Audit] ${txError}`);
                } else {
                    transactions = txData.transactions || [];
                    console.log(`[Audit] ${transactions.length} transactions fetched`);
                }
            } catch(e) {
                txError = `Fetch: ${e.message}`;
                console.warn('[Audit] Transactions fetch failed:', e.message);
            }

            // Transform API format → auditor format
            if (!data || typeof data !== 'object') throw new Error('Invalid API response');
            let saldoData = ApiTransform.transformSaldo(data.saldo);
            let resultatData = ApiTransform.transformResultat(data.resultat, data.months);

            // Transform previousYear if available (YoY rules)
            let previousYear = ApiTransform.transformPreviousYear(data.previousYear, data.months);
            let vatMonthly = data.vatMonthly || [];
            ({ saldo: saldoData, resultat: resultatData, previousYear, vatMonthly, transactions } = strictPrepared ? strictPrepared.input : prepareApiAuditData(data, transactionResponse, {
                clientName: expectedName, companyId: client.companyId, companyKey: client.companyKey, year, monthFrom, monthTo,
            }, dataSource));

            const cName = client.name || client.clientName || '';
            const orgNr = resolveAuditOrg(client, kundeliste, apiClients);
            const brregMeta = await fetchBrregMeta(orgNr);
            const priorYearProjects = await fetchPriorYearProjects({ fetchFn: fetchRapporter, source: dataSource,
                request: JSON.parse(reqBody), transactions });
            const vatSource = hasVatOrg(orgNr) ? await loadVatSource(authFetch, userRole, { year, monthTo }) : null;
            const otpYtd = await fetchOtpYtd({fetchFn:fetchRapporter, source:dataSource, request:{...JSON.parse(reqBody),...(dataSource==='24so'?{auditContract:'audit-data/v1'}:{})}});
            let auditMeta = { ...buildAuditMeta(cName, orgNr, brregMeta, { year, monthFrom, monthTo }), priorYearProjects, otpYtd, sourceEvidence:auditSourceEvidence(dataSource,JSON.parse(reqBody),data,transactionResponse), source:dataSource, sourceIdentity:String(JSON.parse(reqBody)[sourceConfig(dataSource).idField] || ''),
                vatSubmission: vatSubmissionFor(vatSource, orgNr, { year, monthTo }, cName) };
            if (strictPrepared) auditMeta = prepareAudit({source:dataSource,data,lines:transactionResponse,expected:strictExpected,meta:auditMeta}).auditMeta;
            setSystem('24seven');
            setParsedData({ saldo: saldoData, resultat: resultatData });
            setFiles({ saldo: `API: ${cName}`, resultat: `API: ${cName}` });
            setReportCompanyName(cName);
            setAuditPeriod({ year: Number(year), monthFrom: Number(monthFrom), monthTo: Number(monthTo) });
            setBatchFromDashboard(false);
            setReportClientId(getClientId(client, dataSource));
            const result = { ...runAudit('24seven', saldoData, resultatData, disabledRules, previousYear, vatMonthly, transactions, txError, auditMeta), auditedAt: Date.now() };
            result.issues = attachIssueTranslations(result.issues, otherLang(), () => runAudit('24seven', saldoData, resultatData, disabledRules, previousYear, vatMonthly, transactions, txError, auditMeta));
            showReport(result, {
                system: '24seven',
                sourceLabel: sourceConfig(dataSource).label,
                saldo: saldoData,
                resultat: resultatData,
                disabledRules,
                previousYear,
                vatMonthly,
                transactions,
                txError,
                auditMeta,
            });
            setView('report');
            setGlobalExpand(true);

            // Save to localStorage for "Load Last Audit"
            try {
                localStorage.setItem('audit-last', JSON.stringify({
                    system: '24seven',
                    parsedData: { saldo: saldoData, resultat: resultatData },
                    report: result,
                    date: new Date().toLocaleDateString(),
                    sysName: (client.name || client.clientName || '') + ' (API)',
                    companyName: cName,
                    clientId: getClientId(client, dataSource),
                    auditMeta,
                    period: { year: Number(year), monthFrom: Number(monthFrom), monthTo: Number(monthTo) },
                    auditInput: { system: '24seven', sourceLabel: sourceConfig(dataSource).label, saldo: saldoData, resultat: resultatData,
                        previousYear, vatMonthly, transactions, txError, auditMeta },
                }));
            } catch(e) {}

            // Keep the dashboard scoped to its selected period, even if this single
            // report was requested for another period or the user changed the filter.
            const singlePeriod = { year: Number(year), monthFrom: Number(monthFrom), monthTo: Number(monthTo) };
            const singleAuditResult = {
                ...auditWriteToken(saveBaseline, getClientId(client, dataSource)), source: dataSource,
                identityId: getClientId(client, dataSource),
                companyName: cName,
                orgNr,
                auditMeta,
                companyForm: auditMeta.companyForm,
                auditedAt: result.auditedAt,
                score: result.score,
                scoreBreakdown: result.scoreBreakdown,
                totalPenalty: result.totalPenalty,
                errCount: result.errCount,
                warnCount: result.warnCount,
                infoCount: result.infoCount,
                issues: result.issues,
                passedRules: result.passedRules,
                saldo: saldoData,
                resultat: resultatData,
                vatMonthly,
                transactions,
                previousYear, txError,
                status: 'done',
                system: sourceConfig(dataSource).label,
            };
            setBatchResults(prev => {
                if (!sameBatchPeriod(batchPeriodRef.current, singlePeriod)) return prev;
                const existing = prev.filter(r => r.identityId !== getClientId(client, dataSource));
                return [...existing, singleAuditResult];
            });
            const savedSingle = await syncBatchResultsToServer([singleAuditResult], { force: true,
                period: { year: Number(year), monthFrom: Number(monthFrom), monthTo: Number(monthTo) } });
            if (!savedSingle) addToast('Zapis historii audytu nie powiódł się - spróbuj ponownie przed wysyłką.', 'error');

            addToast(t('label_audit_done', client.name || client.clientName), 'success');
        } catch(err) {
            console.error('API fetch error:', err);
            // Błąd z kodem RAPPORTERA niesie już zdanie dla księgowego (pusty okres, zajęte 24SO).
            // Preserve the previous result when the new audit failed.
            // wszystkich lokalnych audytów na serwer i mogłaby nadpisać nowszy wynik z innej karty.
            addToast(err.code ? err.message : t('api_data_error'), 'error');
        } finally {
            setApiLoading(false);
        }
    };

    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
    const ALLOWED_MIME_TYPES = [
        'text/csv', 'text/plain', 'application/csv',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        '' // Niektóre przeglądarki nie ustawiają MIME
    ];

    const handleFilesDrop = async (fileList) => {
        if (!system) { addToast(t('selectSystem'), 'warning'); return; }
        const config = SYSTEMS[system];
        const filesArray = Array.from(fileList);
        const parsePromises = [];

        for (const originalFile of filesArray) {
            // --- WALIDACJA PLIKU ---
            if (originalFile.size > MAX_FILE_SIZE) {
                addToast(t('toast_file_too_big', originalFile.name, (originalFile.size / 1024 / 1024).toFixed(1)), 'error');
                continue;
            }
            if (originalFile.type && !ALLOWED_MIME_TYPES.includes(originalFile.type)) {
                addToast(t('toast_bad_mime', originalFile.name, originalFile.type), 'error');
                continue;
            }

            let fileToProcess = originalFile;

            // --- HIDDEN EXCEL CONVERSION START ---
            if (originalFile.name.match(/\.xlsx?$/i)) {
                try {
                    const csvContent = await ExcelProcessor.convertFile(originalFile);
                    const newName = originalFile.name.replace(/\.xlsx?$/i, '.csv');
                    fileToProcess = new File([csvContent], newName, { type: "text/csv;charset=utf-8;" });
                    console.log(`Converted ${originalFile.name} to virtual ${newName} for processing.`);
                } catch (e) {
                    addToast(t('toast_excel_error', originalFile.name, e.message), 'error');
                    continue;
                }
            }
            // --- HIDDEN EXCEL CONVERSION END ---

            // Wrap PapaParse w Promise aby uniknąć race condition
            const parsePromise = new Promise((resolve) => {
                window.Papa.parse(fileToProcess, {
                    delimiter: ";",
                    skipEmptyLines: true,
                    complete: (results) => {
                        const content = results.data.map(r => r.join(';')).slice(0, 20).join('\n');
                        let type = null;
                        if (system === '24seven') {
                            if (content.includes('Totalt') && content.includes('%')) type = 'resultat';
                            else if (content.includes('IB') && content.includes('UB')) type = 'saldo';
                        } else if (system === 'fiken') {
                            if (content.includes('Driftsinntekter')) type = 'resultat';
                            else if (content.includes('Inngående') && content.includes('Utgående')) type = 'saldo';
                        } else if (system === 'tripletex') {
                            if (content.includes('t.o.m')) type = 'resultat';
                            else if (content.includes('Utgående saldo')) type = 'saldo';
                        } else if (system === 'unimicro') {
                            if (content.toLowerCase().includes('jan') && content.toLowerCase().includes('sum')) type = 'resultat';
                            else if (content.includes('I.B') || content.includes('Debet')) type = 'saldo';
                        }

                        if (!type) { addToast(t('toast_unknown_type', fileToProcess.name, config.name), 'error'); resolve(null); return; }

                        try {
                            let parsed = [];
                            if (type === 'saldo') {
                                let startIdx = 0;
                                if(system === 'unimicro') startIdx = results.data.findIndex(r => r.join(';').includes('Konto')) + 1;
                                else if(system === 'fiken') startIdx = results.data.findIndex(r => r.join(';').includes('Inngående')) + 1;
                                else if(system === 'tripletex') startIdx = results.data.findIndex(r => r.join(';').includes('Kontonummer')) + 1;
                                else startIdx = 1;
                                if (startIdx < 1) startIdx = 0;
                                parsed = config.parseSaldo(results.data.slice(startIdx));
                            } else {
                                let hIdx = 0;
                                if(system === '24seven') hIdx = results.data.findIndex(r => r.includes('Totalt'));
                                else if(system === 'fiken') hIdx = results.data.findIndex(r => r.join(';').includes('Driftsinntekter'));
                                else if(system === 'tripletex') hIdx = results.data.findIndex(r => r.includes('Kontonummer'));
                                else if(system === 'unimicro') hIdx = results.data.findIndex(r => r.some(c => String(c || '').toLowerCase().includes('jan')));
                                if (hIdx === -1) throw new Error(t('toast_header_missing'));
                                parsed = config.parseResultat(results.data[hIdx], results.data.slice(hIdx + 1));
                            }
                            if (parsed.length === 0) {
                                addToast(t('toast_no_accounts', fileToProcess.name, config.name), 'error');
                                resolve(null); return;
                            }
                            // Deduplikacja kont — zachowaj ostatnie wystąpienie
                            const seen = new Map();
                            parsed.forEach(acc => seen.set(acc.id, acc));
                            parsed = Array.from(seen.values());
                            resolve({ type, parsed, fileName: fileToProcess.name });
                        } catch (e) { addToast(t('toast_parse_error', fileToProcess.name, e.message), 'error'); resolve(null); }
                    }
                });
            });
            parsePromises.push(parsePromise);
        }

        // Czekaj na wszystkie parsowania, potem aktualizuj state jednorazowo
        const results = await Promise.all(parsePromises);
        const newParsedData = { ...parsedData };
        const newFiles = { ...files };
        let anyLoaded = false;

        results.filter(Boolean).forEach(r => {
            newParsedData[r.type] = r.parsed;
            newFiles[r.type] = r.fileName;
            addToast(t('toast_loaded', r.type, r.parsed.length, r.fileName), 'success');
            anyLoaded = true;
        });

        if (anyLoaded) {
            setParsedData(newParsedData);
            setFiles(newFiles);
        }
    };

    const generateReport = (companyName) => {
        if (isAuditing) return;
        auditStateRevisionRef.current++;
        setIsAuditing(true);
        setAuditPeriod(null); // CSV mode — no period info
        setBatchFromDashboard(false);
        setReportCompanyName(companyName || ''); // CSV — use provided name or empty
        setReportClientId(null); // A file name cannot establish an accounting company identity.
        // Ostrzeżenie o niepełnym audycie
        if (parsedData.saldo.length === 0 && parsedData.resultat.length > 0) {
            addToast(t('toast_missing_saldo'), 'warning');
        } else if (parsedData.resultat.length === 0 && parsedData.saldo.length > 0) {
            addToast(t('toast_missing_resultat'), 'warning');
        }
        const auditMeta = buildAuditMeta(companyName || '', '');
        const result = { ...runAudit(system, parsedData.saldo, parsedData.resultat, disabledRules, null, [], [], null, auditMeta), auditedAt: Date.now() };
        // Audyt z pliku też zapamiętuje swoje dane, żeby zmiana języka liczyła go tak samo.
        showReport(result, {
            system, saldo: parsedData.saldo, resultat: parsedData.resultat, disabledRules,
            previousYear: null, vatMonthly: [], transactions: [], txError: null, auditMeta,
        });
        setView('report');
        setGlobalExpand(true);

        // Zapisz do localStorage
        try {
            localStorage.setItem('audit-last', JSON.stringify({
                system,
                parsedData,
                report: result,
                date: new Date().toLocaleDateString(),
                sysName: SYSTEMS[system].name,
                companyName,
                clientId: null,
                auditInput: { system, saldo: parsedData.saldo, resultat: parsedData.resultat,
                    previousYear: null, vatMonthly: [], transactions: [], txError: null, auditMeta },
            }));
        } catch(e) { /* quota exceeded — ignoruj */ }

        // Save only the report just computed, never the rest of a cached portfolio.
        if (companyName) {
            const csvId = `csv-${companyName.replace(/\s/g, '_')}`;
            const csvAuditResult = {
                identityId: csvId, companyName, orgNr: '', auditMeta,
                companyForm: auditMeta.companyForm,
                auditedAt: result.auditedAt,
                score: result.score, scoreBreakdown: result.scoreBreakdown,
                totalPenalty: result.totalPenalty,
                errCount: result.errCount, warnCount: result.warnCount, infoCount: result.infoCount,
                issues: result.issues, passedRules: result.passedRules,
                saldo: parsedData.saldo, resultat: parsedData.resultat,
                status: 'done', system, engineSystem: system,
            };
            setBatchResults(prev => {
                const existing = prev.filter(r => r.identityId !== csvId);
                return [...existing, csvAuditResult];
            });
            // CSV has no server-verified identity or reporting period. Keep it local.
        }
        setIsAuditing(false);
    };

    const loadLastAudit = () => {
        try {
            const saved = JSON.parse(localStorage.getItem('audit-last'));
            if (!saved || !saved.system || !SYSTEMS[saved.system]) return;
            setSystem(saved.system);
            setParsedData(saved.parsedData);
            const restored = restoreLastAudit(saved, disabledRules);
            setAuditPeriod(restored.period);
            setBatchFromDashboard(false);
            setReportCompanyName(saved.companyName || '');
            setReportClientId(saved.clientId || null);
            showReport(restored.report, restored.input);
            setView('report');
            setGlobalExpand(true);
            addToast(t('toast_last_loaded'), 'success');
        } catch(e) { /* uszkodzone dane — ignoruj */ }
    };

    const hasLastAudit = (() => {
        try {
            const saved = JSON.parse(localStorage.getItem('audit-last'));
            return saved && saved.system && SYSTEMS[saved.system];
        } catch(e) { return false; }
    })();

    const downloadParsedCSV = (type) => {
        const csv = Helpers.generateCSV(parsedData[type], type);
        if (csv) Helpers.downloadFile(csv, `PARSED_${system}_${type}.csv`);
        else addToast(t('toast_no_data_type', type), 'warning');
    };

    const downloadAllZip = async () => {
        if (!parsedData.saldo.length && !parsedData.resultat.length) {
            addToast(t('toast_no_data'), 'warning');
            return;
        }
        const zip = new window.JSZip();
        if (parsedData.saldo.length) {
            const csvSaldo = Helpers.generateCSV(parsedData.saldo, 'saldo');
            zip.file(`PARSED_${system}_Saldobalanse.csv`, csvSaldo);
        }
        if (parsedData.resultat.length) {
            const csvResultat = Helpers.generateCSV(parsedData.resultat, 'resultat');
            zip.file(`PARSED_${system}_Resultatrapport.csv`, csvResultat);
        }
        try {
            const content = await zip.generateAsync({ type: "blob" });
            const url = URL.createObjectURL(content);
            const link = document.createElement("a");
            link.href = url;
            link.download = `PARSED_${system}_ALL.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Błąd tworzenia ZIP:", err);
            addToast(t('toast_zip_error'), 'error');
        }
    };

    // PDF EXPORT — capture the on-screen report with pdf-mode class
    const [pdfGenerating, setPdfGenerating] = useState(false);

    const downloadPDF = async () => {
        const evidenceStates = [];
        const detailStates = [];
        if (pdfGenerating || !report) return;
        setPdfGenerating(true);

        try {
            // Dynamically load html2pdf.js (CDN)
            if (!window.html2pdf) {
                await new Promise((resolve, reject) => {
                    const script = document.createElement('script');
                    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
                    script.onload = resolve;
                    script.onerror = reject;
                    document.head.appendChild(script);
                });
            }

            const el = document.getElementById('report-printable') || document.querySelector('[data-report-content]');
            if (!el) { addToast(t('pdf_no_report'), 'error'); return; }

            // Inject branded header + footer directly into the report element (temporary)
            const now = new Date();
            const dateStr = now.toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit', year: 'numeric' });
            const timeStr = now.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' });
            const periodLabel = auditPeriod ? formatPeriodShort(auditPeriod.monthTo, auditPeriod.year) : dateStr;
            const sysName = system ? (SYSTEMS[system]?.name || 'Finago') : 'Finago';
            const presentedReport = reportForPresentation(report);
            const presentation = auditPresentation(presentedReport, (ruleId, issue) =>
                matchingAck(_ackStore[reportClientId] || [], issue, auditPeriod));
            const scoreColor = presentation.gaugeColor;

            // Escape HTML to prevent XSS in innerHTML
            const esc = (s) => String(s || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');

            const header = document.createElement('div');
            header.id = '_pdf_header';
            header.innerHTML = `
                <div style="background:linear-gradient(135deg,#AA5D26,#C68E4D);color:#fff;padding:24px 32px;border-radius:8px;margin-bottom:16px">
                    <div style="font-size:11px;font-weight:700;letter-spacing:2px;opacity:0.8;margin-bottom:10px">MTA AUDIT PRO</div>
                    <div style="font-size:20px;font-weight:700;margin-bottom:4px">${esc(reportCompanyName) || t('pdf_report_title')}</div>
                    <div style="font-size:12px;opacity:0.9">${esc(sysName)} · ${esc(periodLabel)} · ${t('pdf_generated')}: ${esc(dateStr)} ${esc(timeStr)}</div>
                </div>
                <div style="display:flex;gap:12px;margin-bottom:16px;align-items:center">
                    <div style="width:52px;height:52px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:20px;font-weight:700;color:#fff;background:${scoreColor};flex-shrink:0">${presentation.score.toFixed(1)}</div>
                    <div>
                        <div style="font-size:14px;font-weight:700;color:#334155">${presentation.score.toFixed(1)} / 10.0</div>
                        <div style="font-size:11px;color:#64748B">${esc(String(presentedReport.errCount || 0))} ${t('label_feil')} · ${esc(String(presentedReport.warnCount || 0))} ${t('label_advarsler')} · ${(presentedReport.issues || []).filter(i => i.type === 'info' || i.type === 'success').length} ${t('label_info')}</div>
                        <div style="font-size:11px;color:#64748B">${esc(t(presentation.verdictKey, presentedReport.errCount || 0))}</div>
                        ${presentation.signals > 0 ? '<div style="font-size:11px;color:#64748B">' + esc(t('audit_review_notice', presentation.signals)) + '</div>' : ''}
                        ${presentation.unchecked > 0 ? '<div style="font-size:11px;color:#64748B">' + esc(t('audit_coverage_notice', presentation.unchecked)) + '</div>' : ''}
                    </div>
                </div>`;

            const footer = document.createElement('div');
            footer.id = '_pdf_footer';
            footer.innerHTML = `<div style="margin-top:20px;padding:12px 0;border-top:2px solid #E2E8F0;text-align:center;font-size:10px;color:#94A3B8">MTA Audit Pro · mtagroup.no · ${dateStr} ${timeStr}</div>`;

            // Insert header at top, footer at bottom
            el.prepend(header);
            el.appendChild(footer);

            // Add pdf-mode + expand evidence
            document.body.classList.add('pdf-mode');
            el.querySelectorAll('[data-evidence]').forEach(e => {
                evidenceStates.push([e, e.style.display]); e.style.display = 'block';
            });
            el.querySelectorAll('details').forEach(e => {
                detailStates.push([e, e.open]); e.open = true;
            });
            await new Promise(r => setTimeout(r, 300));

            const companySlug = reportCompanyName ? reportCompanyName.replace(/[^a-zA-Z0-9]/g, '_') : 'Report';
            await window.html2pdf().set({
                margin: [6, 6, 10, 6],
                filename: `Audit_${companySlug}_${new Date().toISOString().slice(0, 10)}.pdf`,
                image: { type: 'jpeg', quality: 0.92 },
                html2canvas: { scale: 2, useCORS: true, backgroundColor: '#FFFFFF', scrollY: 0 },
                jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
                pagebreak: {
                    mode: ['css', 'legacy'],
                    avoid: ['tr', 'thead', '.verdict-banner', '[data-expand-toggle="true"]'],
                },
            }).from(el).save();

            // Cleanup — remove injected header/footer, restore pdf-mode
            header.remove();
            footer.remove();
            document.body.classList.remove('pdf-mode');
            addToast(t('toast_pdf_ok'), 'success');
        } catch (err) {
            console.error('[PDF export] error:', err);
            addToast(t('toast_pdf_error'), 'error');
            document.body.classList.remove('pdf-mode');
            const h = document.getElementById('_pdf_header'); if (h) h.remove();
            const f = document.getElementById('_pdf_footer'); if (f) f.remove();
        } finally {
            for (const [el, open] of detailStates) el.open = open;
            for (const [el, display] of evidenceStates) el.style.display = display;
            setPdfGenerating(false);
        }
    };

    // --- REPORT ACK FUNCTIONS ---
    // ScreenReport owns loading for the actual report period.
    const ackClientId = reportClientId || null;

    // --- POPULATE _appCtx (legacy) + AppContext (new) ---
    const _ctxValue = {
        // Batch state
        batchResults, setBatchResults, batchRunning, setBatchRunning, batchProgress, batchAbortRef,
        batchPeriod, setBatchPeriod, batchExpandedId, setBatchExpandedId,
        batchSelectedIds, setBatchSelectedIds, batchFromDashboard, setBatchFromDashboard,
        batchHistory, setBatchHistory, confirmDialog, setConfirmDialog,
        // API state
        apiClients, apiConnected, apiLoading, setApiLoading, apiSelectedClient,
        setApiClients, setDataSource,
        // User/settings
        userRole, accountantName, kundeliste, saveKundeliste, disabledRules,
        auditWindow, auditLocked, setAuditLocked, dataSource, findKundeMatch,
        // View/report
        fetchClientList,
        view, setView, system, setSystem, reportSource, files, setFiles,
        parsedData, setParsedData, report, setReport,
        reportClientId, setReportClientId, reportCompanyName, setReportCompanyName,
        globalExpand, setGlobalExpand, lang, addToast,
        // Functions
        handleApiFetchAndAudit, handleApiConnect, getDataEndpoint, runBatchWithPool, syncBatchResultsToServer,
        loadAuditRun, openFirmReport, showStoredAudit, setReportSource, loadLastAudit,
        // ScreenReport extras
        showPreviewModal, setShowPreviewModal, downloadPDF, pdfGenerating, auditPeriod, setAuditPeriod,
        reportAcksApp, setReportAcksApp,
        // ScreenHome extras
        manualUploadClient, setManualUploadClient,
        // ScreenSettings extras
        toggleRule, setAllRules, fetchKundeliste, getAccountant,
        dragActive, setDragActive, isAuditing, generateReport, handleFilesDrop,
        auditTeam, saveAuditTeam,
        // ScreenBatchDashboard extras
        setCompareIds, setShowCompare, isInactive, isHolding, isAuditableClient,
        // ScreenReport ack functions (must be fresh — they close over ackClientId)
        ackClientId,
    };
    setAppCtx(_ctxValue); // legacy — screens that still use getAppCtx()

    // Stable component references — freeze on first render, prevents unmount/mount on App re-render
    const _sr = useRef(null);
    if (!_sr.current) {
        _sr.current = {
            ScreenBatchDashboard, ScreenHome, ScreenReport, ScreenSettings,
            ScreenReports, ScreenUsers, ScreenAckSummary, ScreenAuditLog,
            ScreenAckActivity, ScreenPermanentAcksLog, ScreenMyFirms,
        };
    }
    const {
        ScreenBatchDashboard: SBatchDash, ScreenHome: SHome, ScreenReport: SReport,
        ScreenSettings: SSettings, ScreenReports: SReports, ScreenUsers: SUsers,
        ScreenAckSummary: SAckSummary, ScreenAckActivity: SAckActivity, ScreenAuditLog: SAuditLog,
        ScreenPermanentAcksLog: SPermAcks, ScreenMyFirms: SMyFirms,
    } = _sr.current;

    // --- LOADING STATE ---
    if (!appReady) return (
        <div className="flex h-screen bg-[#FAFAFA] overflow-hidden">
            <aside style={{ width: '60px', flexShrink: 0 }} className="bg-white border-r border-[#E5E7EB]"></aside>
            <main className="flex-1 flex items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                    <div className="w-8 h-8 border-2 border-[#AA5D26] border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-sm text-[#9CA3AF]">{t('auth_loading')}</p>
                </div>
            </main>
        </div>
    );

    // --- MAIN RENDER ---
    return (
        <AppContext.Provider value={_ctxValue}>
        <div className="flex h-screen bg-[#FAFAFA] overflow-hidden">
            <AppSidebar
                view={view}
                setView={setView}
                lang={lang}
                setLang={setLang}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                batchDoneCount={batchResults.filter(r => r.status === 'done').length}
                batchRunning={batchRunning}
                userRole={userRole}
                feedbackNewCount={feedbackNewCount}
            />
            <main id="main-content" className="flex-1 min-w-0 min-h-0 relative overflow-y-auto" role="main">
                <Suspense fallback={<div className="flex items-center justify-center h-full p-8 text-sm text-[#9CA3AF]">{t('loading') || 'Laster...'}</div>}>
                {view === 'home' && <SHome />}
                {view === 'my-firms' && <SMyFirms />}
                {view === 'report' && <SReport key={[reportClientId, auditPeriod?.year, auditPeriod?.monthFrom, auditPeriod?.monthTo].join(':')} />}
                {view === 'settings' && <SSettings />}
                {view === 'batch-dashboard' && <SBatchDash />}
                {view === 'reports' && <SReports />}
                {view === 'admin-users' && userRole === 'admin' && <SUsers />}
                {view === 'admin-ack-summary' && userRole === 'admin' && <SAckSummary />}
                {view === 'admin-work-activity' && userRole === 'admin' && <SAckActivity />}
                {view === 'admin-activity' && userRole === 'admin' && <SAuditLog />}
                {view === 'admin-permanent-acks' && userRole === 'admin' && <SPermAcks />}
                {view === 'admin-notifications' && userRole === 'admin' && <ScreenTracking />}
                {view === 'admin-audit-history' && userRole === 'admin' && <SAuditHistory />}
                </Suspense>

                {/* Data Preview Modal */}
                {showPreviewModal && (
                    <DataPreviewModal
                        saldo={parsedData.saldo}
                        resultat={parsedData.resultat}
                        onClose={() => setShowPreviewModal(false)}
                    />
                )}

                {/* Manual Upload Modal — rendered from ScreenHome via _appCtx */}
                {/* manualUploadClient is handled inside ScreenHome */}

                {/* === COMPARE VIEW OVERLAY === */}
                {showCompare && compareIds.length >= 2 && (() => {
                    const compareFirms = compareIds.map(id => {
                        const r = batchResults.find(br => br.identityId === id);
                        const cl = apiClients.find(c => c.identityId === id);
                        if (!r) return null;
                        return {
                            identityId: r.identityId,
                            companyName: r.companyName || cl?.name || cl?.clientName || '',
                            orgNr: r.orgNr || cl?.organizationNumber || '',
                            score: r.score ?? null,
                            errCount: r.errCount ?? 0,
                            warnCount: r.warnCount ?? 0,
                            infoCount: r.infoCount ?? 0,
                            categorySummary: r.categorySummary ?? {},
                            issues: r.issues ?? [],
                            system: r.system || 'Finago',
                        };
                    }).filter(Boolean);
                    if (compareFirms.length < 2) return null;
                    const scores = compareFirms.map(f => f.score).filter(s => s !== null);
                    const bestScore = Math.max(...scores);
                    const worstScore = Math.min(...scores);
                    const catLabels = { compliance: t('cat_compliance'), balance: t('cat_balance'), result: t('cat_result'), financial: t('cat_financial'), analysis: t('cat_analysis') };
                    const catDotCmp = (catSummary, catId) => {
                        const cat = catSummary?.[catId];
                        if (!cat) return 'bg-slate-200 dark:bg-slate-700';
                        if (cat.errors > 0) return 'bg-red-500';
                        if (cat.warnings > 0) return 'bg-amber-400';
                        return 'bg-emerald-500';
                    };
                    return (
                        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowCompare(false)}
                            onKeyDown={e => { if (e.key === 'Escape') setShowCompare(false); }} tabIndex={-1} ref={el => el && el.focus()}>
                            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
                                {/* Compare Header */}
                                <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between bg-slate-50 dark:bg-slate-800 flex-shrink-0">
                                    <h2 className="text-lg font-bold font-heading dark:text-white flex items-center gap-2">
                                        <Icon name="columns" className="w-5 h-5 text-copper-500" />
                                        {t('compare_title')} ({compareFirms.length})
                                    </h2>
                                    <button onClick={() => setShowCompare(false)} className="p-2 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors">
                                        <Icon name="x" className="w-5 h-5" />
                                    </button>
                                </div>
                                {/* Compare Body */}
                                <div className="flex-1 overflow-y-auto p-6">
                                    <div className={`grid gap-4 ${compareFirms.length === 2 ? 'grid-cols-1 md:grid-cols-2' : compareFirms.length === 3 ? 'grid-cols-1 md:grid-cols-3' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'}`}>
                                        {compareFirms.map(firm => {
                                            const isBest = firm.score === bestScore && scores.length > 1;
                                            const isWorst = firm.score === worstScore && scores.length > 1 && bestScore !== worstScore;
                                            const borderClass = isBest ? 'ring-2 ring-emerald-500' : isWorst ? 'ring-2 ring-red-400' : 'ring-1 ring-slate-200 dark:ring-slate-700';
                                            return (
                                                <div key={firm.identityId} className={`rounded-xl p-4 bg-white dark:bg-slate-800 ${borderClass} flex flex-col gap-3`}>
                                                    {/* Firm name + system */}
                                                    <div>
                                                        <h3 className="font-bold text-sm font-heading dark:text-white truncate">{firm.companyName}</h3>
                                                        <div className="flex items-center gap-2 text-[10px] text-slate-400 mt-0.5">
                                                            {firm.orgNr && <span className="font-mono">{firm.orgNr}</span>}
                                                            <span className="px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-700 font-mono">{firm.system}</span>
                                                        </div>
                                                    </div>
                                                    {/* Score gauge */}
                                                    <div className="flex items-center gap-3">
                                                        <ScoreGauge score={firm.score ?? 0} />
                                                        <div>
                                                            <div className="text-xl font-bold font-heading dark:text-white">{firm.score ?? '\u2014'}/10</div>
                                                            <div className="text-[10px] text-slate-400">{firm.errCount}E \u00B7 {firm.warnCount}W \u00B7 {firm.infoCount}I</div>
                                                        </div>
                                                    </div>
                                                    {/* Category rows */}
                                                    <div className="space-y-1">
                                                        {['compliance','balance','result','financial','analysis'].map(cat => {
                                                            const cs = firm.categorySummary?.[cat];
                                                            return (
                                                                <div key={cat} className="flex items-center gap-2 text-xs">
                                                                    <span className={`w-3 h-3 rounded-full flex-shrink-0 ${catDotCmp(firm.categorySummary, cat)}`} />
                                                                    <span className="text-slate-600 dark:text-slate-300 flex-1 truncate">{catLabels[cat] || cat}</span>
                                                                    {cs ? <span className="font-mono text-slate-400 text-[10px]">{cs.errors || 0}E {cs.warnings || 0}W</span> : <span className="text-slate-300 text-[10px]">\u2014</span>}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                    {/* Top 5 issues */}
                                                    <div>
                                                        <div className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 mb-1 uppercase tracking-wider">{t('compare_top_issues')}</div>
                                                        <div className="space-y-1">
                                                            {firm.issues
                                                                .filter(i => (i.type === 'error' || i.type === 'warning' || i.specialType === 'review_signal') && i.specialType !== 'financial_result' && i.specialType !== 'scoring')
                                                                .slice(0, 5)
                                                                .map((issue, idx) => (
                                                                    <div key={idx} className={`flex items-start gap-1.5 text-[11px] px-2 py-1 rounded ${issue.type === 'error' ? 'bg-red-50 dark:bg-red-900/10 text-red-600 dark:text-red-300' : 'bg-amber-50 dark:bg-amber-900/10 text-amber-600 dark:text-amber-300'}`}>
                                                                        <Icon name={issue.type === 'error' ? 'alert-circle' : 'alert-triangle'} className="w-3 h-3 flex-shrink-0 mt-0.5" />
                                                                        <span className="truncate">{getIssueTitle(issue)}</span>
                                                                    </div>
                                                                ))
                                                            }
                                                            {firm.issues.filter(i => (i.type === 'error' || i.type === 'warning' || i.specialType === 'review_signal') && i.specialType !== 'financial_result' && i.specialType !== 'scoring').length === 0 && (
                                                                <div className="text-[10px] text-slate-400 italic">{t('compare_no_issues')}</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                                {/* Compare Footer */}
                                <div className="px-6 py-3 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 flex justify-end flex-shrink-0">
                                    <button onClick={() => setShowCompare(false)} className="px-4 py-2 text-sm rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 font-medium transition-colors">
                                        {t('compare_close')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })()}

                {/* Confirm Dialog */}
                <ConfirmDialog open={!!confirmDialog} title={confirmDialog?.title} message={confirmDialog?.message}
                    onConfirm={confirmDialog?.onConfirm} onCancel={() => setConfirmDialog(null)}
                    confirmLabel={confirmDialog?.confirmLabel} danger={confirmDialog?.danger} />

                {/* Loading overlay */}
                {apiLoading && !batchRunning && (
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
                        <div className="bg-white rounded-2xl p-8 shadow-2xl flex flex-col items-center gap-4 max-w-sm mx-4">
                            <div className="w-12 h-12 border-4 border-[#AA5D26] border-t-transparent rounded-full animate-spin"></div>
                            <p className="text-lg font-heading font-bold text-slate-800">{t('api_fetching_data')}</p>
                            <p className="text-sm text-slate-500 text-center">{t('batch_running')}</p>
                        </div>
                    </div>
                )}

                {/* Toast Notifications */}
                <ToastContainer toasts={toasts} removeToast={removeToast} />
            </main>

            {/* Side panel for expanded batch firm */}
            {batchExpandedId && (() => {
                const firm = batchResults.find(r => r.identityId === batchExpandedId);
                if (!firm || firm.status !== 'done') return null;
                // Re-run audit with current rules for consistent score
                let panelData = firm;
                if (firm.saldo && firm.saldo.length > 0) {
                    try {
                        const fresh = rerunStoredAudit(firm, disabledRules, firm.auditMeta || buildAuditMeta(firm.companyName, firm.orgNr));
                        panelData = { ...firm, ...fresh };
                    } catch {}
                }
                const cid = panelData.identityId || panelData.orgNr || panelData.companyName;
                const issues = withSavedFindingEvidence(panelData.issues || []).filter(i => !['financial_result','scoring','health'].includes(i.specialType));
                const errors = issues.filter(i => i.type === 'error');
                const warnings = issues.filter(i => i.type === 'warning');
                const infos = issues.filter(i => i.type === 'info');
                return (
                    <>
                        <div className="fixed inset-0 z-40 bg-black/30" onClick={() => setBatchExpandedId(null)} />
                        <div className="fixed right-0 top-0 h-full w-full md:w-[45%] z-50 bg-white shadow-2xl flex flex-col overflow-hidden border-l border-slate-200">
                            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50 flex-shrink-0">
                                <div>
                                    <h2 className="font-bold text-lg">{firm.companyName}</h2>
                                    <span className="text-xs text-slate-500 font-mono">{firm.orgNr}</span>
                                </div>
                                <button onClick={() => setBatchExpandedId(null)} className="p-2 rounded-lg hover:bg-slate-200">{'\u2715'}</button>
                            </div>
                            <div className="flex-1 overflow-y-auto p-5 space-y-3">
                                {/* Score + metrics */}
                                <div className="flex items-center gap-3">
                                    <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold text-white ${scoreBgColor(pickScore(panelData))}`}>
                                        {pickScore(panelData).toFixed(1)}
                                    </div>
                                    <div>
                                        <div className="text-sm font-bold">{pickScore(panelData).toFixed(1)} / 10.0</div>
                                        <div className="text-xs text-slate-500">{panelData.errCount || 0} {t('hero_errors')} · {panelData.warnCount || 0} {t('label_advarsler')}</div>
                                    </div>
                                </div>
                                <FirmTimeline identityId={firm.identityId} companyName={firm.companyName} currentScore={firm.score} />
                                <div className="flex gap-2 mb-4">
                                    <button onClick={() => {
                                        setReportCompanyName(firm.companyName || '');
                                        setReportClientId(firm.identityId || firm.orgNr || null);
                                        setAuditPeriod(batchPeriod);
                                        setBatchFromDashboard(true);
                                        setSystem('24seven');
                                        setFiles({ saldo: `Batch: ${firm.companyName}`, resultat: `Batch: ${firm.companyName}` });
                                        // Re-run audit with current rules for consistent score
                                        showStoredAudit(firm, (firm.saldo || []).length > 0
                                            ? rerunStoredAudit(firm, disabledRules, firm.auditMeta || buildAuditMeta(firm.companyName, firm.orgNr))
                                            : { issues: firm.issues || [], score: firm.score, errCount: firm.errCount, warnCount: firm.warnCount, infoCount: firm.infoCount, categorySummary: firm.categorySummary, passedRules: firm.passedRules });
                                        setBatchExpandedId(null);
                                        setView('report');
                                        setGlobalExpand(true);
                                    }} className="flex-1 px-4 py-2 text-xs rounded-lg bg-copper-500 text-white hover:bg-copper-600 font-medium">
                                        <Icon name="file-text" className="w-4 h-4 inline mr-1" /> {t('nav_report') || 'Rapport'}
                                    </button>
                                    {userRole === 'admin' && (
                                        <button onClick={async () => {
                                            if (!confirm(t('confirm_reset_acks') || 'Nullstill alle godkjenninger for denne klienten?')) return;
                                            const r = await authFetch(`/api/audit-ack/client/${encodeURIComponent(cid)}`, { method: 'DELETE' });
                                            if (r.ok) {
                                                const data = await r.json();
                                                delete _ackStore[cid];
                                                addToast(`${data.deleted} ${t('ack_reset_done') || 'godkjenninger nullstilt'}`, 'success');
                                                setBatchExpandedId(null);
                                            }
                                        }} className="px-3 py-2 text-xs rounded-lg border border-slate-200 text-slate-500 hover:text-red-600 hover:border-red-200 transition-colors" title={t('ack_reset') || 'Nullstill godkjenninger'}>
                                            <Icon name="undo-2" className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                                {(() => {
                                    const _errors = issues.filter(i => i.type === 'error');
                                    const _warnings = issues.filter(i => i.type === 'warning');
                                    const _infos = issues.filter(i => i.type === 'info');
                                    const ackable = issues.filter(i => i.ruleId && (i.type === 'error' || i.type === 'warning' || i.specialType === 'review_signal'));
                                    const ackedCount = ackable.filter(i => isResolvedAck(matchingAck(_ackStore[cid], i, batchPeriod))).length;
                                    return (
                                        <div className="space-y-2">
                                            <div className="flex items-center justify-between">
                                                <h3 className="text-sm font-semibold">{t('review_progress')} ({ackable.length})</h3>
                                                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${ackedCount === ackable.length && ackable.length > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                                                    {ackedCount}/{ackable.length}
                                                </span>
                                            </div>
                                            <div className="flex gap-2 text-[10px]">
                                                {_errors.length > 0 && <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-semibold">{_errors.length} {t('hero_errors')}</span>}
                                                {_warnings.length > 0 && <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">{_warnings.length} {t('label_advarsler')}</span>}
                                                {_infos.length > 0 && <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-semibold">{_infos.length} {t('label_info')}</span>}
                                            </div>
                                            {ackable.length > 0 && (
                                                <div className="w-full bg-slate-200 rounded-full h-1.5">
                                                    <div className="bg-emerald-500 h-1.5 rounded-full transition-all" style={{width: `${Math.round(ackedCount/ackable.length*100)}%`}}></div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}
                                {/* Grouped by type like full report */}
                                {errors.length > 0 && <div className="text-[10px] font-bold text-red-600 uppercase tracking-wider mt-3 mb-1">{t('tier_critical')} ({errors.length})</div>}
                                {errors.map((issue, i) => {
                                    const ack = matchingAck(_ackStore[cid], issue, batchPeriod);
                                    return (
                                        <div key={'e'+i} className={`p-3 rounded-lg text-xs bg-red-50 text-red-700 ${isResolvedAck(ack) ? 'opacity-40' : ''}`}>
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="font-medium">{getIssueTitle(issue)}</div>
                                                {ack && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold flex-shrink-0">{ack.action === 'client_followup' ? t('ack_client_followup') : ack.action === 'checked' ? `✓ ${t('ack_checked')}` : `🔧 ${t('ack_fixed')}`}</span>}
                                            </div>
                                        </div>
                                    );
                                })}
                                {warnings.length > 0 && <div className="text-[10px] font-bold text-amber-600 uppercase tracking-wider mt-3 mb-1">{t('tier_check')} ({warnings.length})</div>}
                                {warnings.map((issue, i) => {
                                    const ack = matchingAck(_ackStore[cid], issue, batchPeriod);
                                    return (
                                        <div key={'w'+i} className={`p-3 rounded-lg text-xs bg-amber-50 text-amber-700 ${isResolvedAck(ack) ? 'opacity-40' : ''}`}>
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="font-medium">{getIssueTitle(issue)}</div>
                                                {ack && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold flex-shrink-0">{ack.action === 'client_followup' ? t('ack_client_followup') : ack.action === 'checked' ? `✓ ${t('ack_checked')}` : `🔧 ${t('ack_fixed')}`}</span>}
                                            </div>
                                        </div>
                                    );
                                })}
                                {infos.length > 0 && <div className="text-[10px] font-bold text-blue-600 uppercase tracking-wider mt-3 mb-1">{t('tier_info')} ({infos.length})</div>}
                                {infos.map((issue, i) => {
                                    const ack = matchingAck(_ackStore[cid], issue, batchPeriod);
                                    return (
                                        <div key={'i'+i} className={`p-3 rounded-lg text-xs bg-blue-50 text-blue-700 ${isResolvedAck(ack) ? 'opacity-40' : ''}`}>
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="font-medium">{getIssueTitle(issue)}</div>
                                                {ack && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold flex-shrink-0">{ack.action === 'client_followup' ? t('ack_client_followup') : ack.action === 'checked' ? `✓ ${t('ack_checked')}` : `🔧 ${t('ack_fixed')}`}</span>}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </>
                );
            })()}
        </div>
        </AppContext.Provider>
    );
};

export default App;
